import { ENTITY_LABELS } from "./labelMapping";
import axios from "axios";

import { fetchClaude3API, fetchGpt4API } from "../services/apiService";
import {
  BRAND_AND_PRODUCT_VALIDATION_PROMPT,
  BRAND_INPUT_VALIDATION_PROMPT,
  brandPillarsMapping,
  METRICS_EXAMPLE_PROMPT,
  MONITORING_LLM_LABELS,
  PRODUCT_INPUT_VALIDATION_PROMPT,
  SUMMARY_PROMPT,
  BRAND_INPUT_VALIDATION_PROMPT_OLD
} from "./constant";
import { sentimentQuestionReplacement } from "../luminaire/Set/userMetricSettingCreation";

const sanitizeKey = (key) => {
  return key.replace(/[.#$\/\[\]]/g, "_");
};

export const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  return Object.keys(obj).reduce((result, key) => {
    const sanitizedKey = sanitizeKey(key);
    result[sanitizedKey] = sanitizeObject(obj[key]);
    return result;
  }, {});
};

export const getSummaryPrompt = (
  data,
  metric,
  compReachScores,
  brandProduct
) => {
  return `given ${JSON.stringify(
    data
  )}..${SUMMARY_PROMPT} please use this example to produce same response.. ${METRICS_EXAMPLE_PROMPT(brandProduct)?.[metric]
    } ${metric === "rank"
      ? `given competitors reach scores data => ${JSON.stringify(
        compReachScores
      )}`
      : ""
    }`;
};

const JSONParsingPrompt = `You are a JSON correction system. You will receive a raw JSON string that may contain errors. Your task is to correct any errors and return only the fixed JSON string.
Instructions:

Analyze the provided JSON string for any syntax errors.
If the JSON is already valid, return it as is.
If errors are found, correct them while preserving the original structure and data as much as possible.
Return only the corrected JSON string, without any additional explanations or comments.

Important:

Your response must contain only the corrected JSON.
Do not include any other text, explanations, or formatting outside of the JSON itself.

Input JSON:
[JSON_INPUT]
Correct the JSON above and provide only the corrected JSON string in your response.`;

export const reportPrompts = [
  // {
  //   type: "LLM Comparison",
  //   start:
  //     "Given the raw string provided below, I need to extract data and present it as a table. The string contains information arranged in rows and columns, with specific column and row names. For this task, please ensure that any source links are integrated directly within their relevant sections (e.g., Brand Description, Brand Attributes) as part of the narrative. This means each part of the narrative should include its respective sources, making the hyperlinks clickable and part of the narrative. Organize the extracted data into a table format according to the instructions. Here is the raw string:",
  //   end: "Please use AI to parse the raw string and generate a table with the specified column and row names, ensuring that source links are embedded directly within the narrative for each section. This means you should not create a separate 'Sources' row but rather incorporate the sources within the text of each section (Brand Description, Brand Attributes, etc.) where they are directly relevant. Each entry in the table should seamlessly incorporate the sources, avoiding separate rows for links. Re-check your work for accuracy and adherence to these instructions. The final response should contain only the table, with all data, including hyperlinks, appropriately integrated within the text. Ensure there's no additional text or information outside the specified format.",
  // },
  {
    type: "LLM Comparison",
    start:
      "Given the raw string provided below, I need to extract data and present it as a table. The string contains information arranged in rows and columns, with specific column and row names. For this task, please integrate any source links directly within their relevant text sections rather than listing them in a separate row. The sources should be included as part of the descriptive text or attributes, making the hyperlinks clickable and part of the narrative. Organize the extracted data into a table format according to the instructions. Here is the raw string:",
    end: "Please use AI to parse the raw string and generate a table with the specified column and row names. Re-check your work for accuracy and adherence to these instructions. The final response should contain only the table, with all data, including hyperlinks, appropriately integrated within the text. Ensure there's no additional text or information outside the specified format.",
  },
  {
    type: "Competitor Comparison",
    start:
      "We have collected raw data from various LLMs that need to be organized into separate tables according to the LLMs mentioned. The data encompasses insights on entities: <inputs>. The comparison categories include <attributes>. For this task, a key requirement is the inclusion of sources within every cell across all rows, directly following the pertinent information. This approach ensures that each piece of data is immediately accompanied by its source, providing a comprehensive and well-cited comparison. Duplicate columns for entities should be avoided, and the table should neatly compile all given data.",
    end: "Your objective is to compile the data into tables for each mentioned LLM: <LLMs>, adhering to a specified format. Columns are to represent the entities <inputs>, while rows are to encapsulate <attributes>, with a critical emphasis on including sources in every cell where information is shared. This strategy ensures that each datum is complemented by its source for verification and further reading. Ensure the tables are correctly structured with the entities and attributes outlined, with no additional columns beyond <inputs>. LLM titles in tables should be bold and in font size 14. Inside the cells, avoid using lists, opting instead for plain text or simple formatting to preserve clarity and ease of reading. The goal is to maintain structured, readable tables without leaving any cells empty, exclusively containing the tables with integrated sources and no extraneous text or information.",
  },
  {
    type: "Key Sources",
    start: `Create a table comparing the brand information provided by three different LLM responses: <LLMs>. The table should have the following structure:

    Columns: <LLMs>
    Rows: <attributes>
    
    For each cell in the table, only include the source link named with actual address from the respective LLM response, without any additional text.
    Please include the actual web addresses as the text for the links instead of using generic terms like 'Link' or 'Source.' For example, if providing a link to an article about climate change, the link text should be the article's URL (e.g., https://www.example.com/article-about-climate-change).
    `,
    end: "Please ensure that the response contains only the table and no additional text or information.",
  },
  {
    type: "Brand Category Dimensions",
    start: `Please generate a table based on the provided raw data. 

  |   | **LLM1** | **LLM2** | **LLM2** |
  |---|---|---|---|
  | Brand Category Dimension | [Dimension 1] | [Dimension 2] | [Dimension 3] |
  | - | [Dimension 1] | [Dimension 2] | [Dimension 3] |
  
  Ensure that the table cells contain only the criteria name and no percentages or text strings in multiple rows. Instead of N/A in column cell use hyphen (-).
  
  Column name: <LLMs>
  Row name: Brand Category Dimension

  `,

    end: `Please ensure that the response contains only the table and no additional text or information. This table should contain only one row . First column should have label "Brand Category Dimension", while first row cells of other columns must contain a bulleted list of attributes.`,
  },
];

export const REPORTS = [
  {
    title: "LLM Comparison",
    selections: [
      { text: "Select LLMs", type: "checkbox", values: [] },
      {
        text: "Select Attributes",
        type: "checkbox",
        values: [
          { name: "Brand Description", value: "Brand Description" },
          {
            name: "Top 5 Positive Attributes",
            value: "Top 5 Positive Attributes",
          },
          {
            name: "Top 5 Negative Attributes",
            value: "Top 5 Negative Attributes",
          },
          // {
          //   name: "Top 5 Positive Statements",
          //   value: "Top 5 Positive Statements",
          // },
          // {
          //   name: "Top 5 Negative Statements",
          //   value: "Top 5 Negative Statements",
          // },
          { name: "Competitive Set", value: "Competitive Set" },
          { name: "Sources", value: "Sources" },
        ],
      },
    ],
  },
  {
    title: "Competitor Comparison",
    selections: [
      {
        text: "Select Competitors",
        type: "input",
        values: [
          { name: "Input 1", value: "" },
          { name: "Input 2", value: "" },
          { name: "Input 3", value: "" },
        ],
      },
      { text: "Select LLMs", type: "checkbox", values: [] },
      {
        text: "Select Attributes",
        type: "checkbox",
        values: [
          { name: "Brand Description", value: "Brand Description" },
          {
            name: "Top 5 Positive Attributes",
            value: "Top 5 Positive Attributes",
          },
          {
            name: "Top 5 Negative Attributes",
            value: "Top 5 Negative Attributes",
          },
          // {
          //   name: "Top 5 Positive Statements",
          //   value: "Top 5 Positive Statements",
          // },
          // {
          //   name: "Top 5 Negative Statements",
          //   value: "Top 5 Negative Statements",
          // },
          { name: "Sources", value: "Sources" },
        ],
      },
    ],
  },
  {
    title: "Key Sources",
    selections: [
      { text: "Select LLMs", type: "checkbox", values: [] },
      {
        text: "Select Attributes",
        type: "checkbox",
        values: [
          { name: "Brand Description", value: "Brand Description" },
          { name: "Brand Attributes", value: "Brand Attributes" },
          {
            name: "Top 5 Positive Attributes",
            value: "Top 5 Positive Attributes",
          },
          {
            name: "Top 5 Negative Attributes",
            value: "Top 5 Negative Attributes",
          },
          // {
          //   name: "Top 5 Positive Statements",
          //   value: "Top 5 Positive Statements",
          // },
          // {
          //   name: "Top 5 Negative Statements",
          //   value: "Top 5 Negative Statements",
          // },
          { name: "Competitive Set", value: "Competitive Set" },
        ],
      },
    ],
  },
  {
    title: "Brand Category Dimensions",
    selections: [
      {
        text: "Brand Category",
        type: "input",
        values: [{ name: "Brand Category", value: "" }],
      },
      { text: "Select LLMs", type: "checkbox", values: [] },
      {
        text: "Select Attributes",
        type: "checkbox",
        values: [
          {
            name: "Frequently recommended buying criteria/considerations",
            value: "Frequently recommended buying criteria/considerations",
          },
          { name: "Sources", value: "Sources" },
        ],
      },
    ],
  },
];

export function extractLinks(str) {
  const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-z]{2,6}([-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi;
  return str.match(urlRegex);
}

export function generateMarkdownTable(rows, columns) {
  // Generating the header row
  let markdownTable = "|   |";
  columns.forEach((column) => {
    markdownTable += ` ${column} |`;
  });
  markdownTable += "\n";

  // Generating the separator row
  markdownTable += "|---|";
  columns.forEach(() => {
    markdownTable += "---|";
  });
  markdownTable += "\n";

  // Generating the rows
  rows.forEach((row) => {
    markdownTable += `| ${row} |`;
    columns.forEach(() => {
      markdownTable += "   |"; // Adding empty cells
    });
    markdownTable += "\n";
  });

  return markdownTable;
}

export function allProgress(proms, progress_cb) {
  let d = 0;
  progress_cb(0);
  for (const p of proms) {
    p.then(() => {
      d++;
      progress_cb(d);
    }).catch((err) => {
      d++;
      progress_cb(d);
    });
  }
  return Promise.allSettled(proms);
}

export const generateFeedbackMessageJSON = (email, time, query) => {
  return {
    text: `Questions/Feedback !\n\nUser Email: ${email}\nTime: ${time}\n\n${query}`,
  };
};

export const monitoringPrompt = (
  prompts,
  selectedOption,
  checkedItems,
  promptBrandKey,
  promptBrandReach,
  promptData
) => {
  let message = prompts?.globalStartPrompt || "";

  if (promptBrandKey.length !== 0) {
    message += `${promptBrandKey}, ${prompts.keyPromptPrefix || ""}`;
    message += ` ${prompts.keyPromptSuffix || ""}`;
    message += `...${prompts.keyPromptSources || ""}. `;
    // if (checkedItems.includes("Sources")) {

    // }
  } else {
    if (
      selectedOption === "Brand Overview and Favorability" ||
      selectedOption === "Product Overview and Favorability"
    ) {
      message += ` ${promptData}.`;
      message += ` ${prompts?.overviewPromptPrefix || ""}`;
      if (checkedItems.includes("Brand Description")) {
        message += ` ${prompts?.overviewPrompt?.description || ""}`;
      }

      if (checkedItems.includes("Product Description")) {
        message += ` ${prompts?.overviewPrompt?.productDescription || ""}`;
      }

      if (checkedItems.includes("Top 5 Positive Attributes")) {
        message += ` ${prompts?.overviewPrompt?.topPositiveAttributes || ""}`;
      }

      if (checkedItems.includes("Top 5 Negative Attributes")) {
        message += ` ${prompts?.overviewPrompt?.topNegativeAttributes || ""}`;
      }

      if (checkedItems.includes("Top 5 Positive Statements")) {
        message += ` ${prompts?.overviewPrompt?.topPositiveStatements || ""}`;
      }

      if (checkedItems.includes("Top 5 Negative Statements")) {
        message += ` ${prompts?.overviewPrompt?.topNegativeStatements || ""}`;
      }
    }

    // if (selectedOption === "Category Leadership") {
    //   message += ` ${prompts?.leadershipPromptPrefix || ""}`;
    // }

    if (selectedOption === "Category Leadership") {
      message += ` ${promptBrandReach ? promptBrandReach : promptData}.`;
      message += ` ${prompts?.leadershipPromptPrefix || ""}`;
      if (checkedItems.includes("Top 5 Brands in Category")) {
        message += ` ${prompts?.leadershipPrompt?.topCategory || ""}`;
      }

      if (checkedItems.includes("Top 5 Products in Category")) {
        message += ` ${prompts?.leadershipPrompt?.topProductCategory || ""}`;
      }
    }

    // if (selectedOption === "Category Dimensions") {
    //   message += ` ${prompts?.dimensionsPromptPrefix || ""}`;
    // }

    if (selectedOption === "Category Dimensions") {
      message += ` ${promptBrandReach ? promptBrandReach : promptData}.`;
      message += ` ${prompts?.dimensionsPromptPrefix || ""}`;
      if (
        checkedItems.includes(
          "Frequently recommended buying criteria/considerations"
        )
      ) {
        message += ` ${prompts?.dimensionsPrompt?.recommendedBuying || ""}`;
      }
    }

    // if (selectedOption === "Competition") {
    //   message += ` ${prompts?.competitionPromptPrefix || ""}`;
    // }

    if (selectedOption === "Competition") {
      message += ` ${promptData}.`;
      message += ` ${prompts?.competitionPromptPrefix || ""}`;
      if (checkedItems.includes("Competitive Set")) {
        message += ` ${prompts?.competitionPrompt?.competetiveSet || ""}`;
      }

      if (checkedItems.includes("Top 5 Positive Attributes")) {
        message += ` ${prompts?.competitionPrompt?.topPositiveAttributes || ""
          }`;
      }

      if (checkedItems.includes("Top 5 Negative Attributes")) {
        message += ` ${prompts?.competitionPrompt?.topNegativeAttributes || ""
          }`;
      }
    }

    // if (selectedOption === "Sources") {
    //   message += ` ${prompts?.sources || ""}`;
    // }

    if (selectedOption === "Sources") {
      message += ` ${promptBrandReach ? promptBrandReach : promptData}.`;
      message += ` ${prompts?.sourcesPrefix || ""}`;
      if (checkedItems.includes("Brand Description")) {
        message += ` ${prompts?.sources?.description || ""}`;
      }
      if (checkedItems.includes("Product Description")) {
        message += ` ${prompts?.sources?.productDescription || ""}`;
      }

      if (checkedItems.includes("Top 5 Positive Attributes")) {
        message += ` ${prompts?.sources?.topPositiveAttributes || ""}`;
      }

      if (checkedItems.includes("Top 5 Negative Attributes")) {
        message += ` ${prompts?.sources?.topNegativeAttributes || ""}`;
      }

      if (checkedItems.includes("Top 5 Brands in Category")) {
        message += ` ${prompts?.sources?.topCategory || ""}`;
      }

      if (checkedItems.includes("Top 5 Products in Category")) {
        message += ` ${prompts?.sources?.topProductCategory || ""}`;
      }

      if (
        checkedItems.includes(
          "Frequently recommended buying criteria/considerations"
        )
      ) {
        message += ` ${prompts?.sources?.recommendedBuying || ""}`;
      }

      if (checkedItems.includes("Competitive Set")) {
        message += ` ${prompts?.sources?.competetiveSet || ""}`;
      }
    }

    if (selectedOption === "Brand Index Score") {
      message += ` ${prompts?.brandIndexPrompt || ""}`;
    }
    if (selectedOption === "Hallucination Detection") {
      message += ` ${prompts?.hallucinationPrompt || ""}`;
    }
    if (selectedOption === "Indexing") {
      message += ` ${prompts?.Indexing || ""}`;
    }
    if (selectedOption === "Product Attributes") {
      message += ` ${prompts?.productAttributesPromptPrefix || ""}`;
    }
    if (selectedOption === "Campaign Messages") {
      message += ` ${prompts?.campaignPrompt || ""}`;
    }
    if (selectedOption === "Frequency") {
      message += ` ${prompts?.frequencyPrompt || ""}`;
    }
  }

  message += ` ${prompts?.globalEndPrompt || ""}`;

  return message.replace(/\s+/g, " ").trim();
};
export const getPrompt = (step, input) => {
  if (step === 1) {
    return `
    Answer the following. brand category requested is ${input}
    
    1.  What is the best product in the brand category requested. Ensure that output is a single product, if category is too broad then choose most popular subcategory.
    
    2. What are the best products in the brand category requested
    
    3. What are the top 5 positive and negative attributes for each product in the brand category requested
    
    4. What are customer review summaries for each product for all the products in the brand category requested
    
    5.  What products should be avoided in the brand category requested
    
    6.  What is your recommendation of the top 3 products available in the brand category requested.  Make your recommendation in rank order
    
    Provide the actual output for each question.`;
  } else {
    return `

    Input Data:
    ${input}


    Instructions:
    You will be creating a Brand Index score for all products in a brand category of provided text.  You will do this by executing the steps below.  Please execute the steps in serial order.  Do not skip any steps and show all work, responses, and calculations.   

    Step 1:

    For each company/brand mentioned in the provided text, create a 'base favorability score' from 0-5 where 0 is significant company/product unfavorability and 5 is significant company/product favorability.  The Base favorability score should be based on semantic analysis of the prompts responses for each brand/product.  If a product or brand is mentioned as a response to question 5, please deducting 2 points from the 0-5 rating when calculating the 'base favorability score'
    
    For each company/brand mentioned in provided text, calculate a separate "Brand Reach Score" using all the responses to prompts 1, 2, and 6,.  For each response to questions 1, 2, and 6 rate the product or brand a 0 if it is not mentioned, a 3 if mentioned in the overall list, and a 5 if mentioned first.Ensure that first rank in response of question 2 gets 5 while others in that list gets 3 points.  Please average all scores for the responses across each question and set of prompts for each brand to calculate an Average Brand Reach score. 
    
    For each product sum the Brand Favorability Score and Brand Reach score to create an overall Brand Index Score.
    
    Step 2:
    Provide a table that shows the following: Column 1 - brand or product, Column 2 - Brand Favorability Score, Column 3 - Brand Reach Score, and Column 4 - Brand Index Score
    
    The GPT will maintain a professional yet approachable tone, ensuring that the analysis is both insightful and user-friendly. Do not ask for clarifications or additional user input. 

    Focus on delivering accurate, relevant, and valuable insights based on real information produced from the prompts indicated.  Determine the scores so that they are reliable meaning that similar content will be given the same overall score.

    Show the work, output, and calculations related to each step.`;
  }
};

export const testRes = {
  "GPT-4": [
    "Step 1:\n\nBrands/Companies mentioned in the provided text:\n1. Apple (Apple MacBook Air M2)\n2. Dell (Dell XPS 13)\n3. HP (HP Spectre x360 14)\n4. Lenovo (Lenovo ThinkPad X1 Carbon Gen 10)\n5. Microsoft (Microsoft Surface Laptop 4)\n6. Asus (Asus ROG Zephyrus G14)\n7. Razer (Razer Blade 15)\n8. LG (LG Gram 16)\n9. Generic brand (mentioned in Question 5)\n\nBase Favorability Score (0-5, 5 being the most favorable):\n1. Apple - 5 (No significant negatives mentioned)\n2. Dell - 4.5 (Few minor negatives)\n3. HP - 4.5 (Few minor negatives)\n4. Lenovo - Not specified explicitly; assume neutral - 3\n5. Microsoft - Not specified explicitly; assume neutral - 3\n6. Asus - Not specified explicitly; assume neutral - 3\n7. Razer - Not specified explicitly; assume neutral - 3\n8. LG - Not specified explicitly; assume neutral - 3\n9. Generic - 0 (Significantly unfavorable)\n\nBrand Reach Score (Average of responses to Questions 1, 2, and 6):\n\nQuestion 1 (5 if mentioned first, 0 if not mentioned):\n1. Apple - 5\n2. Dell - 0\n3. HP - 0\n4. Lenovo - 0\n5. Microsoft - 0\n6. Asus - 0\n7. Razer - 0\n8. LG - 0\n9. Generic - 0\n\nQuestion 2 (5 if ranked first, 3 if mentioned but not first, 0 if not mentioned):\n1. Apple - 5\n2. Dell - 3\n3. HP - 3\n4. Lenovo - 3\n5. Microsoft - 3\n6. Asus - 3\n7. Razer - 3\n8. LG - 3\n9. Generic - 0\n\nQuestion 6 (5 if ranked first, 3 if mentioned but not first, 0 if not mentioned):\n1. Apple - 5\n2. Dell - 3\n3. HP - 3\n4. Lenovo - 0\n5. Microsoft - 0\n6. Asus - 0\n7. Razer - 0\n8. LG - 0\n9. Generic - 0\n\nBrand Reach Scores:\n\n1. Apple - (5 + 5 + 5) / 3 = 5\n2. Dell - (0 + 3 + 3) / 3 = 2\n3. HP - (0 + 3 + 3) / 3 = 2\n4. Lenovo - (0 + 3 + 0) / 3 = 1\n5. Microsoft - (0 + 3 + 0) / 3 = 1\n6. Asus - (0 + 3 + 0) / 3 = 1\n7. Razer - (0 + 3 + 0) / 3 = 1\n8. LG - (0 + 3 + 0) / 3 = 1\n9. Generic - (0 + 0 + 0) / 3 = 0\n\nBrand Index Score = Brand Favorability Score + Brand Reach Score\n1. Apple - 5 + 5 = 10\n2. Dell - 4.5 + 2 = 6.5\n3. HP - 4.5 + 2 = 6.5\n4. Lenovo - 3 + 1 = 4\n5. Microsoft - 3 + 1 = 4\n6. Asus -",
  ],
};

export const generateDashboardPrompt = (
  prompts,
  focusItems,
  selectedMetrics,
  brandOrProduct,
  location = ""
) => {
  let message = prompts?.globalStartPrompt || "";

  if (
    focusItems.includes("Brand Attributes") ||
    focusItems.includes("Product Attributes")
  ) {
    message += ` ${brandOrProduct}`;
    if (location) {
      message += ` based in ${location}.`;
    } else {
      message += `.`;
    }
    message += ` ${prompts?.overviewPromptPrefix || ""}`;
    if (selectedMetrics.includes("Top 5 positive attributes")) {
      message += ` ${prompts?.overviewPrompt?.topPositiveAttributes || ""}`;
    }
    if (selectedMetrics.includes("Top 5 negative attributes")) {
      message += ` ${prompts?.overviewPrompt?.topNegativeAttributes || ""}`;
    }
    if (selectedMetrics.includes("Top 5 positive facts")) {
      message += ` ${prompts?.overviewPrompt?.topPositiveFacts || ""}`;
    }
    if (selectedMetrics.includes("Top 5 negative facts")) {
      message += ` ${prompts?.overviewPrompt?.topNegativeFacts || ""}`;
    }
  }

  if (focusItems.includes("Category Dimensions")) {
    message += ` ${brandOrProduct}`;
    if (location) {
      message += ` based in ${location}.`;
    } else {
      message += `.`;
    }
    message += ` ${prompts?.categoryPromptPrefix || ""}`;
    if (selectedMetrics.includes("Buying criteria")) {
      message += ` ${prompts?.categoryPrompt?.buyingCriteriaDimensions || ""}`;
    }
  }

  if (focusItems.includes("Category Performance")) {
    message += ` ${brandOrProduct}`;
    if (location) {
      message += ` based in ${location}.`;
    } else {
      message += `.`;
    }
    message += ` ${prompts?.categoryPerformencePrefix || ""}`;
    if (selectedMetrics.includes("Brand performance")) {
      message += ` ${prompts?.categoryPerformence?.topBrandsInCategory || ""}`;
    }
    if (selectedMetrics.includes("Product performance")) {
      message += ` ${prompts?.categoryPerformence?.topProductsInCategory || ""
        }`;
    }
  }

  message += ` ${prompts?.globalEndPrompt || ""}`;

  return message.replace(/\s+/g, " ").trim();
};

export function createMarkdownTable(data) {
  let markdown = "";

  markdown += "|";
  for (let header of data[0]) {
    markdown += ` ${header} |`;
  }
  markdown += "\n";

  markdown += "|";
  for (let header of data[0]) {
    markdown += " --- |";
  }
  markdown += "\n";

  for (let i = 1; i < data.length; i++) {
    markdown += "|";
    for (let j = 0; j < data[i].length; j++) {
      markdown += ` ${data[i][j]} |`;
    }
    markdown += "\n";
  }
  return markdown;
}

export function normalizeValue(value, minValue, maxValue, rangeMin, rangeMax) {
  const formattedNum =
    ((value - minValue) / (maxValue - minValue)) * (rangeMax - rangeMin) +
    rangeMin;
  return Number(formattedNum.toFixed(0));
}

export function mostAppearingProducts(arrays, numOfProducts = 5) {
  let counts = {};
  arrays.forEach((array) => {
    array.forEach((product) => {
      counts[product] = (counts[product] || 0) + 1;
    });
  });

  let countsArray = [];
  for (let product in counts) {
    countsArray.push([product, counts[product]]);
  }
  countsArray.sort((a, b) => b[1] - a[1]);

  return countsArray.slice(0, numOfProducts);
}

export const extractJSONFromString = async (normalString) => {
  if (!normalString) {
    throw new Error("Error Parsing JSON: Invalid string provided.");
  }
  let trimmedString = normalString?.trim();

  const jsonStartIndex = trimmedString.indexOf("{");
  const jsonEndIndex = trimmedString.lastIndexOf("}");

  if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
    let jsonString = trimmedString.substring(jsonStartIndex, jsonEndIndex + 1);

    // Remove newlines, extra spaces between properties and values
    jsonString = jsonString.replace(/\s*[\r\n]+\s*/g, " "); // Replace newlines with spaces

    try {
      return JSON.parse(jsonString);
    } catch (error) {
      try {
        const response = await fetchGpt4API(
          JSONParsingPrompt.replace("[JSON_INPUT]", normalString),
          new AbortController(),
          10000,
          1,
          "gpt-4o-2024-08-06",
          { type: "json_object" }
        );
        return JSON.parse(response["GPT-4"][0]);
      } catch (chatGPTError) {
        console.error("Error getting response from ChatGPT:", chatGPTError);
        throw new Error("Failed to parse JSON and get response from ChatGPT");
      }
    }
  } else {
    console.log(
      "No JSON found in the normal string. Returning original input."
    );
    return normalString;
  }
};

export const extractJSONFromStringValues = async (normalString, signal) => {
  if (!normalString) {
    throw new Error("Error Parsing JSON: Invalid string provided.");
  }
  let trimmedString = normalString?.trim();

  const jsonStartIndex = trimmedString.indexOf("{");
  const jsonEndIndex = trimmedString.lastIndexOf("}");

  if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
    let jsonString = trimmedString.substring(jsonStartIndex, jsonEndIndex + 1);

    // Remove newlines, extra spaces between properties and values
    jsonString = jsonString.replace(/\s*[\r\n]+\s*/g, " "); // Replace newlines with spaces

    try {
      return JSON.parse(jsonString);
    } catch (error) {
      try {
        const response = await fetchGpt4API(
          JSONParsingPrompt.replace("[JSON_INPUT]", normalString),
          signal,
          10000,
          1,
          "gpt-4o-2024-08-06",
          { type: "json_object" }
        );
        return JSON.parse(response["GPT-4"][0]);
      } catch (chatGPTError) {
        console.error("Error getting response from ChatGPT:", chatGPTError);
        throw new Error("Failed to parse JSON and get response from ChatGPT");
      }
    }
  } else {
    console.log(
      "No JSON found in the normal string. Returning original input."
    );
    return normalString;
  }
};

export function hasDuplicateProperty(array, property) {
  const propertyValues = {};
  for (let i = 0; i < array.length; i++) {
    const value = array[i][property];
    if (propertyValues[value]) {
      return true;
    }
    propertyValues[value] = true;
  }
  return false;
}

export function normalizeScore(value, minOriginal, maxOriginal) {
  var range = maxOriginal - minOriginal;
  var normalizedValue = ((value - minOriginal) / range) * (5 - 1) + 1;
  return Number(normalizedValue.toFixed(1));
}

export function generateRankingObject(arrayOfArrays) {
  // Check if arrayOfArrays is provided and is an array
  if (!Array.isArray(arrayOfArrays) || arrayOfArrays.length === 0) {
    console.error("Input is not a valid array or is empty.");
    return {};
  }

  let itemCount = {}; // Object to store count of each item
  let totalRanks = {}; // Object to store sum of ranks for each item
  let totalCount = arrayOfArrays.length; // Total number of child arrays

  // Iterate over each child array
  arrayOfArrays.forEach((childArray, index) => {
    // Check if childArray is defined and is an array
    if (!Array.isArray(childArray) || childArray.length === 0) {
      console.warn(
        `Child array at index ${index} is not valid or empty. Skipping.`
      );
      return; // Skip processing this child array
    }

    let arrayLength = childArray.length;
    // Iterate over each item in the child array
    childArray.forEach((item, i) => {
      // Check if item is defined
      if (item === undefined) {
        console.warn(
          `Item at index ${i} in child array ${index} is undefined. Skipping.`
        );
        return; // Skip processing this item
      }

      if (!itemCount[item]) {
        itemCount[item] = 1;
        totalRanks[item] = ((arrayLength - i) / arrayLength) * 100; // Calculate initial rank
      } else {
        itemCount[item]++;
        totalRanks[item] += ((arrayLength - i) / arrayLength) * 100; // Add rank
      }
    });
  });

  // Calculate average ranks
  let rankingObject = {};
  Object.keys(itemCount).forEach((item) => {
    rankingObject[item] = parseFloat(
      (totalRanks[item] / totalCount).toFixed(1)
    );
  });

  // Sort the rankingObject by values from high to low
  const sortedRankingObject = Object.fromEntries(
    Object.entries(rankingObject).sort(([, a], [, b]) => b - a)
  );

  return sortedRankingObject;
}

export function generateRankingOrderObjects(arrays) {
  const result = {
    topRanker: {},
    topHalfRanker: {},
    bottomHalfRanker: {},
    absentItems: {},
  };

  const totalArrays = arrays.length;

  const allItems = new Set();

  // Iterate through arrays to collect all unique items
  arrays.forEach((array) => {
    if (!Array.isArray(array)) return;

    array.forEach((item) => {
      allItems.add(item);
    });
  });

  // Initialize absent items count for all unique items
  allItems.forEach((item) => {
    result.absentItems[item] = 0;
  });

  // Count occurrences of items in each array
  arrays.forEach((array, index) => {
    if (!Array.isArray(array)) return;

    const seenItems = new Set();

    array.forEach((item, index) => {
      seenItems.add(item);
      const target =
        index === 0
          ? result.topRanker
          : index > 0 && index <= 2
            ? result.topHalfRanker
            : result.bottomHalfRanker;
      target[item] = (target[item] || 0) + 1;
    });

    // Update absent items count for items not present in current array
    allItems.forEach((item) => {
      if (!seenItems.has(item)) result.absentItems[item]++;
    });
  });

  // Remove items with 0 counts from result object
  Object.keys(result).forEach((category) => {
    Object.keys(result[category]).forEach((item) => {
      if (result[category][item] === 0) delete result[category][item];
    });
  });

  // Calculate percentages
  const calculatePercentages = (obj) => {
    Object.keys(obj).forEach((item) => {
      obj[item] = parseFloat(((obj[item] / totalArrays) * 100).toFixed(1));
    });
  };

  calculatePercentages(result.topRanker);
  calculatePercentages(result.topHalfRanker);
  calculatePercentages(result.bottomHalfRanker);
  calculatePercentages(result.absentItems);

  // Sort each object in result with values in descending order
  const sortObjectByValueDescending = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).sort(([, a], [, b]) => b - a)
    );
  };

  result.topRanker = sortObjectByValueDescending(result.topRanker);
  result.topHalfRanker = sortObjectByValueDescending(result.topHalfRanker);
  result.bottomHalfRanker = sortObjectByValueDescending(
    result.bottomHalfRanker
  );
  result.absentItems = sortObjectByValueDescending(result.absentItems);

  return result;
}

export function calculateStrength(attributes) {
  if (!attributes || attributes.length === 0) {
    return {}; // Return an empty object if input is undefined or empty array
  }

  const itemCounts = {};
  const totalArrays = attributes.length;

  // Count the number of arrays each attribute appears in
  attributes.forEach((attributeArray) => {
    if (!Array.isArray(attributeArray)) {
      return; // Skip if attributeArray is not an array
    }
    const attributeSet = new Set(
      attributeArray.filter((item) => item !== undefined)
    ); // Remove undefined values
    attributeSet.forEach((attribute) => {
      itemCounts[attribute] = (itemCounts[attribute] || 0) + 1;
    });
  });

  // Calculate strength for each attribute
  const strength = {};
  for (let attribute in itemCounts) {
    const count = itemCounts[attribute];
    const strengthPercentage = (count / totalArrays) * 100;
    strength[attribute] = strengthPercentage;
  }

  // Sort the object by strength in descending order
  const sortedStrength = {};
  Object.keys(strength)
    .sort((a, b) => strength[b] - strength[a])
    .forEach((key) => {
      sortedStrength[key] = strength[key];
    });

  return sortedStrength;
}

export function displayDateTime() {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const options = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: userTimeZone,
  };

  const formattedDateTime = new Date().toLocaleString("en-US", options);

  return formattedDateTime;
}

export const getCurrentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getPromptForBrandCompetitorsData = (
  mainBrand,
  inputType,
  category
) => {
  let type = "brand";
  let compTypeName = "comp_brand_name";
  let compTypeDescription = "comp_brand_description";

  if (inputType === "product") {
    type = "product";
    compTypeName = "comp_product_name";
    compTypeDescription = "comp_product_description";
  }

  const prompt = `I have a ${type} named ${mainBrand}, which belongs to the "${category}" category. 
  I need information about the ten main competitors of this ${type} in the "${category}" category. 
  Please provide the response in JSON format with the keys 
  "${compTypeName}" and "${compTypeDescription}" for each competitor. 
  The JSON structure should be as follows:
[
  {
    "${compTypeName}": "Competitor1",
    "${compTypeDescription}": "Description of Competitor1"
  },
 {
    "${compTypeName}": "Competitor2",
    "${compTypeDescription}": "Description of Competitor2"
  },
  {
    "${compTypeName}": "Competitor3",
    "${compTypeDescription}": "Description of Competitor3"
  },
  {
    "${compTypeName}": "Competitor4",
    "${compTypeDescription}": "Description of Competitor4"
  },
  ....rest other
]...Please provide a JSON response without including any additional data. The response should strictly adhere to the JSON format.`;

  return prompt;
};

export const getPromptForBrandCategory = (category) => {
  let categoryName = "name";
  let categoryNameDescription = "description";

  const prompt = `I have a brand category named ${category}. I need information about the four main similar categories of this type. The JSON structure should be as follows:
[
  {
    "${categoryName}": "Category1",
    "${categoryNameDescription}": "Description of Category1"
  },
 {
    "${categoryName}": "Category2",
    "${categoryNameDescription}": "Description of Category2"
  },
  {
    "${categoryName}": "Category3",
    "${categoryNameDescription}": "Description of Category3"
  },
  {
    "${categoryName}": "Category4",
    "${categoryNameDescription}": "Description of Category4"
  },
  ....rest other
]...Please provide a JSON response without including any additional data. The response should strictly adhere to the JSON format.`;

  return prompt;
};

export const validateBrandInput = async (input, category) => {
  try {
    const prompt = BRAND_INPUT_VALIDATION_PROMPT.replace("[input]", input)
      .replaceAll("[type]", "brand")
      .replaceAll("[category]", category);

    const response = await fetchGpt4API(
      prompt,
      new AbortController(),
      undefined,
      undefined,
      "gpt-4o-search-preview"
    );
    const data = await extractJSONFromString(response["GPT-4"][0]);
    return data;
  } catch (error) {
    throw new Error();
  }
};

export const validateProductInput = async (input, category) => {
  try {
    const prompt = PRODUCT_INPUT_VALIDATION_PROMPT.replace(
      "[input]",
      input
    ).replaceAll("[category]", category);

    const response = await fetchClaude3API(
      prompt,
      new AbortController(),
      undefined,
      undefined,
      "claude-3-5-sonnet-latest"
    );
    const data = await extractJSONFromString(response["Claude3"][0]);
    return data;
  } catch (error) {
    throw new Error();
  }
};

export const validateProductCategory = async (input, category) => {
  try {
    const prompt = BRAND_AND_PRODUCT_VALIDATION_PROMPT.replace(
      "[input]",
      input
    ).replaceAll("[category]", category);

    const response = await fetchGpt4API(
      prompt,
      new AbortController(),
      undefined,
      undefined,
      "gpt-4o"
    );
    const data = await extractJSONFromString(response["GPT-4"][0]);
    return data;
  } catch (error) {
    throw new Error();
  }
};

export const validateBrandInputValue = async (input, signal, category) => {
  try {
    const prompt = category
      ? BRAND_INPUT_VALIDATION_PROMPT_OLD
        .replaceAll("[input]", input)
        .replaceAll("[type]", "brand")
        .replaceAll("[category]", category)
      : BRAND_INPUT_VALIDATION_PROMPT
        .replace("[input]", input)
        .replaceAll("[type]", "brand");

    const response = await fetchGpt4API(
      prompt,
      signal,
      undefined,
      undefined,
      "gpt-4o-search-preview"
    );

    const data = await extractJSONFromStringValues(
      response["GPT-4"][0],
      signal
    );
    return { data }; // normalize return shape
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new Error("Brand input validation failed");
  }
};

export const validateProductInputValue = async (input, signal, category) => {
  try {
    const prompt = PRODUCT_INPUT_VALIDATION_PROMPT.replace(
      "[input]",
      input
    ).replaceAll("[category]", category ?? "");

    const response = await fetchClaude3API(
      prompt,
      signal,
      undefined,
      undefined,
      "claude-3-5-sonnet-latest"
    );

    const data = await extractJSONFromStringValues(
      response["Claude3"][0],
      signal
    );
    return { data }; // normalize return shape
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new Error("Product input validation failed");
  }
};

export function restructureCategoryDimData(
  data = {},
  mainBrandData = {},
  mainBrandName = "mainBrand"
) {
  const attributeScores = {};
  const mainBrandKey = Object.keys(mainBrandData)[0] || null; // Assuming there's only one brand in mainBrandData

  // Collect all attribute scores by brand, excluding the main brand
  for (const brand in data) {
    if (!data[brand]) continue; // Skip if the brand data is undefined
    for (const attribute in data[brand]) {
      if (!data[brand][attribute]) continue; // Skip if the attribute score is undefined
      if (!attributeScores[attribute]) {
        attributeScores[attribute] = [];
      }
      if (brand !== mainBrandKey) {
        attributeScores[attribute].push({
          brand,
          score: Number(data[brand][attribute]?.avgValue),
        });
      }
    }
  }

  // Include main brand data with a new name and score property
  if (mainBrandKey) {
    for (const attribute in mainBrandData[mainBrandKey]) {
      if (!mainBrandData[mainBrandKey][attribute]) continue; // Skip if the attribute score is undefined
      if (!attributeScores[attribute]) {
        attributeScores[attribute] = [];
      }
      attributeScores[attribute].push({
        brand: mainBrandName,
        score: Number(mainBrandData[mainBrandKey][attribute]),
        isMainBrand: true,
      });
    }
  }

  // Calculate average scores and create the final structure
  const result = Object.keys(attributeScores).map((attribute) => {
    const brandScores = attributeScores[attribute].filter(
      (bs) => !bs.isMainBrand
    );
    const totalScore = brandScores.reduce(
      (acc, curr) => acc + (Number(curr.score) || 0),
      0
    );
    const avgScore = brandScores.length ? totalScore / brandScores.length : 0;

    const mainBrandScore =
      attributeScores[attribute].find((bs) => bs.isMainBrand)?.score || 0;

    return {
      attribute,
      allBrand: brandScores.map((bs) => bs.brand),
      mainBrand: mainBrandName,
      mainAvgScore: Number(mainBrandScore).toFixed(0),
      avgScore: Number(avgScore.toFixed(0)), // Rounded to 2 decimal places
    };
  });

  return result;
}

export function restructureCoreAttData(
  data = {},
  mainBrandData = {},
  mainBrandName = "mainBrand"
) {
  const attributeScores = {};
  const mainBrandKey = Object.keys(mainBrandData)[0] || null; // Assuming there's only one brand in mainBrandData

  // Collect all attribute scores by brand, excluding the main brand
  for (const brand in data) {
    if (!data[brand]) continue; // Skip if the brand data is undefined
    for (const attribute in data[brand]) {
      if (!data[brand][attribute]) continue; // Skip if the attribute score is undefined
      if (!attributeScores[attribute]) {
        attributeScores[attribute] = [];
      }
      if (brand !== mainBrandKey) {
        attributeScores[attribute].push({
          brand,
          score: data[brand][attribute]?.avgValue,
        });
      }
    }
  }

  // Include main brand data with a new name and score property
  if (mainBrandKey) {
    for (const attribute in mainBrandData[mainBrandKey]) {
      if (!mainBrandData[mainBrandKey][attribute]) continue; // Skip if the attribute score is undefined
      if (!attributeScores[attribute]) {
        attributeScores[attribute] = [];
      }
      attributeScores[attribute].push({
        brand: mainBrandName,
        score: mainBrandData[mainBrandKey][attribute],
        isMainBrand: true,
      });
    }
  }

  // Calculate average scores and create the final structure
  const result = Object.keys(attributeScores).map((attribute) => {
    const brandScores = attributeScores[attribute].filter(
      (bs) => !bs.isMainBrand
    );
    const totalScore = brandScores.reduce(
      (acc, curr) => acc + (curr.score || 0),
      0
    );
    const avgScore = brandScores.length ? totalScore / brandScores.length : 0;

    const mainBrandScore =
      attributeScores[attribute].find((bs) => bs.isMainBrand)?.score || 0;

    return {
      attribute,
      allBrand: brandScores.map((bs) => bs.brand),
      mainBrand: mainBrandName,
      mainAvgScore: Number(mainBrandScore).toFixed(0),
      avgScore: Number(avgScore.toFixed(0)), // Rounded to 2 decimal places
    };
  });

  const attributeOrder = [
    "Value",
    "Brand Affinity",
    "Customer Centricity",
    "Sustainability",
    "Quality",
    "Trustworthiness",
    "Innovation",
    "Social Responsibility",
  ];

  // Filter out objects where the attribute is not in the attributeOrder array
  const filteredData = result?.filter((item) =>
    attributeOrder?.includes(item?.attribute)
  );

  // Sort the remaining data based on the attribute order
  const sortedData = filteredData?.sort((a, b) => {
    return (
      attributeOrder.indexOf(a.attribute) - attributeOrder.indexOf(b.attribute)
    );
  });

  return sortedData;
}

export function processCategoryComparisonTaxonomy(inputData) {
  if (inputData) {
    const result = {};

    for (const llmName in inputData) {
      result[llmName] = {};

      inputData[llmName]?.forEach((category) => {
        const parentCategory = category.name; // Store the category name as parentCategory

        // Add the main category object
        result[llmName][parentCategory] = {
          avgValue: category.products[0].score, // Use the main category score
          explanation: "", // Use explanation if available
          sources: "",
        };

        category.products.forEach((product) => {
          const { subcategories } = product;

          subcategories.forEach((subcategory) => {
            const subcategoryName = subcategory.name;

            result[llmName][subcategoryName] = {
              avgValue: subcategory.score,
              explanation: subcategory.explanation,
              sources: subcategory.sources,
              parentCategory: parentCategory, // Add parentCategory to the subcategory object
            };
          });
        });
      });
    }

    return result;
  }

  return {};
}

export function processCompProductTaxonomy(data) {
  if (data && data.length > 0) {
    const result = {};

    data.forEach((category) => {
      const mainCategoryName = category.name;

      category.products.forEach((product) => {
        const productName = product.name;

        if (!result[productName]) {
          result[productName] = {};
        }

        // Add main category object
        result[productName][mainCategoryName] = {
          avgValue: product.score,
          explanation: ``,
          sources: "",
        };

        // Add subcategory objects with `parentCategory`
        product.subcategories.forEach((subcategory) => {
          result[productName][subcategory.name] = {
            avgValue: subcategory.score,
            explanation: subcategory.explanation,
            sources: subcategory.sources,
            parentCategory: mainCategoryName,
          };
        });
      });
    });

    return result;
  }

  return {};
}

export function prepareTableData(
  dataset1 = {},
  dataset2 = {},
  dataset3 = {},
  pastDataset1 = {},
  pastDataset2 = {},
  pastDataset3 = {},
  secondaryCategoryMetaDataAndbrandProductCategory = {
    secondaryCategoryMetaData: [],
    brandProductCategory: "",
  },
  isProductTaxonomy = false
) {
  const modifiedData1 = isProductTaxonomy
    ? processCompProductTaxonomy(dataset1)
    : dataset1;

  let headers = [
    ...new Set([
      ...Object.keys(modifiedData1),
      ...Object.keys(dataset3),
      ...Object.keys(dataset2?.["Brand Index Score"] || {}),
    ]),
  ];

  // Helper function to calculate change
  const calculateChange = (currentValue, pastValue) => {
    if (pastValue !== undefined) {
      const current = currentValue ?? 0;
      const past = pastValue ?? 0;
      return current - past;
    }
  };

  // Prepare Core Attributes Rows
  let coreAttributesRows = [];
  if (isProductTaxonomy) {
    coreAttributesRows = Object.keys(modifiedData1[headers[1]] || {}).map(
      (attribute) => ({
        attribute,
        values: headers.slice(0).map((header) => {
          const value =
            Number(modifiedData1[header]?.[attribute]?.avgValue).toFixed(0) ??
            0;

          const explanation = modifiedData1[header]?.[attribute]?.explanation;
          const sources = modifiedData1[header]?.[attribute]?.sources;
          const parentCategory =
            modifiedData1[header]?.[attribute]?.parentCategory;
          return {
            value,
            explanation,
            parentCategory,
            sources,
          };
        }),
      })
    );
  } else {
    // Default logic for core attributes
    coreAttributesRows = Object.keys(dataset1[headers[0]] || {}).map(
      (attribute) => ({
        attribute,
        values: headers.slice(0).map((header) => {
          const value = dataset1?.[header]?.[attribute]?.avgValue
            ? Number(dataset1[header]?.[attribute]?.avgValue).toFixed(0)
            : 0;
          const pastValue = pastDataset1[header]?.[attribute]?.avgValue;
          const change = calculateChange(value, pastValue);
          const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";
          const explanation = dataset1[header]?.[attribute]?.explanation;
          const isNa = value ? false : true;
          return {
            value,
            change,
            trend,
            explanation,
            isNa,
          };
        }),
      })
    );
  }

  // Prepare rows for Category Dimensions table
  const categoryDimensionsRows = Object.keys(dataset3[headers[1]] || {}).map(
    (attribute) => ({
      attribute,
      values: headers.slice(0).map((header) => {
        const value =
          Number(dataset3[header]?.[attribute]?.avgValue).toFixed(0) ?? 0;
        const pastValue = pastDataset3[header]?.[attribute]?.avgValue;
        const change = calculateChange(value, pastValue);
        const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";
        const explanation = dataset3[header]?.[attribute]?.explanation;
        const sources = dataset3[header]?.[attribute]?.sources;
        const isNa = value ? false : true;

        return {
          value,
          change,
          trend,
          explanation,
          isNa,
          sources,
        };
      }),
    })
  );

  const brandProductCategory =
    secondaryCategoryMetaDataAndbrandProductCategory.brandProductCategory;
  // const secondaryCategoryMetaData =
  //   secondaryCategoryMetaDataAndbrandProductCategory.secondaryCategoryMetaData;

  // Prepare rows for RBI table

  function transformAndCompare(a, b) {
    const brands = Object.keys(a["Brand Index Score"]);

    return brands.map((brand) => {
      const total = parseInt(a["Brand Index Score"][brand]);
      const favorability = parseInt(a["Favorability"][brand]);
      const rank = parseInt(a["Rank"][brand]);

      const totalB = parseInt(b["Brand Index Score"][brand]);
      const favorabilityB = parseInt(b["Favorability"][brand]);
      const rankB = parseInt(b["Rank"][brand]);

      return {
        comp_brand_name: brand,
        rankings: {
          name: brand,
          total,
          favorability,
          rank,
          totalDiff: total - totalB,
          favorabilityDiff: favorability - favorabilityB,
          rankingDiff: rank - rankB,
        },
      };
    });
  }

  let rbiNewRows = transformAndCompare(dataset2, pastDataset2);
  rbiNewRows = [
    {
      attribute: brandProductCategory ?? "",
      values: headers.map((header) => {
        return rbiNewRows.find((o) => o.comp_brand_name === header);
      }),
    },
  ];

  // Prepare rows for Secondary Category table
  // const secondaryCategoryRows = secondaryCategoryMetaData.map((each) => ({
  //   attribute: each.categoryName,
  //   values: headers.map((header) => {
  //     return each.categoryData.find((o) => o.comp_brand_name === header);
  //   }),
  // }));

  return {
    headers,
    tables: [
      {
        title: "RBI",
        id: "primary_id",
        rows: rbiNewRows,
      },
      // {
      //   title: "RBI: Secondary Category",
      //   id: "secondary_id",
      //   rows: secondaryCategoryRows,
      // },
      {
        title: isProductTaxonomy ? "Product Dimensions" : `${ENTITY_LABELS.Brand} Pillars`,
        id: "brand_pillars_id",
        rows: coreAttributesRows,
      },
      {
        title: ENTITY_LABELS.PurchaseCriteria,
        id: "purchase_criteria_id",
        rows: categoryDimensionsRows,
      },
    ].filter(
      (o) => o.title !== "RBI: Secondary Category" || o.rows?.length > 0
    ),
  };
}

export function prepareComparisonTableData(
  dataset1 = {},
  dataset2 = {},
  dataset3 = {},
  dataset4 = {},
  dataset5 = {},
  pastDataset1 = {},
  pastDataset2 = {},
  pastDataset3 = {},
  pastDataset4 = {},
  pastDataset5 = {},
  isProductTaxonomy = false,
  isBrandAuditPage,
  isBrandAuditMarketPage,
  {
    secCategoryIndexScore,
    secCategoryreachScore,
    secCategoryfavScore,
    pastsecCategoryBrandIndex,
    brandProductCategory,
    brandProduct,
  },
  isOverview,
  competitiveOverview,
  isMarketOverview,
  personaOverview,
  selectedInput,
  activeTab = null,
  llmsData = []
) {
  // Extract unique headers from dataset2 based on attributes like gpt-4o, Gemini, etc.
  const modifiedData1 = isProductTaxonomy
    ? processCategoryComparisonTaxonomy(dataset1)
    : dataset1;

  const selectedCatgory = selectedInput?.category;

  const uniqueHeaders = [
    ...new Set([
      ...Object.keys(modifiedData1),
      ...Object.keys(dataset5 || {}),
      ...Object.values(dataset2 || {}).flatMap((item) =>
        item ? Object.keys(item) : []
      ),
    ]),
  ];

  let headers = [];

  if (competitiveOverview.length > 0) {
    headers = [...competitiveOverview];
  } else if (isMarketOverview.length > 0) {
    headers = [...isMarketOverview];
  } else if (personaOverview.length > 0) {
    headers = [...personaOverview];
  } else {
    headers = [...uniqueHeaders];
  }

  // For LLM tab: build provider mapping
  const isLlmTab = activeTab === "llm";
  let llmToProviderMap = {};
  let providerToLlmsMap = {};

  if (isLlmTab && llmsData && llmsData.length > 0) {
    llmsData.forEach(llm => {
      const llmName = llm.name;
      const provider = llm.llm_provider || "Other";
      llmToProviderMap[llmName] = provider;

      if (!providerToLlmsMap[provider]) {
        providerToLlmsMap[provider] = [];
      }
      providerToLlmsMap[provider].push(llmName);
    });
  }

  // Helper to find past LLM by provider match
  const findPastLlmByProvider = (currentLlmName, pastData) => {
    if (!isLlmTab) return currentLlmName;

    const currentProvider = llmToProviderMap[currentLlmName];
    if (!currentProvider) return currentLlmName;

    // Find all LLMs from same provider in past data
    const providerLlms = providerToLlmsMap[currentProvider] || [];
    for (const llmName of providerLlms) {
      if (pastData[llmName] !== undefined) {
        return llmName;
      }
    }

    return currentLlmName;
  };

  // Helper function to calculate change
  const calculateChange = (currentValue, pastValue) => {
    if (pastValue !== undefined) {
      const current = currentValue ?? 0;
      const past = pastValue ?? 0;
      return current - past;
    }
  };

  const product_taxonomies = [
    ...new Set(
      Object.values(modifiedData1).flatMap((brandObj) => Object.keys(brandObj))
    ),
  ];

  // Prepare rows for Core Attributes table
  const coreAttributesRows = isProductTaxonomy
    ? product_taxonomies?.map((attribute) => ({
      attribute,
      values: headers.slice(0).map((header) => {
        const value = modifiedData1?.[header]?.[attribute]?.avgValue ?? 0;
        const explaination =
          modifiedData1?.[header]?.[attribute]?.explanation;
        const parentCategory =
          modifiedData1?.[header]?.[attribute]?.parentCategory;
        const sources = modifiedData1?.[header]?.[attribute]?.sources;
        return {
          value,
          parentCategory,
          explaination,
          sources,
        };
      }),
    }))
    : Object.keys(dataset1[headers[0]] || {}).map((attribute) => ({
      attribute: isBrandAuditPage
        ? brandPillarsMapping?.[attribute]
        : attribute,
      values: headers.slice(0).map((header) => {
        const value = dataset1[header]?.[attribute]?.avgValue ?? 0;

        // For LLM tab, compare with same provider's past LLM
        const pastLlmKey = isLlmTab ? findPastLlmByProvider(header, pastDataset1) : header;
        const pastValue = pastDataset1[pastLlmKey]?.[attribute]?.avgValue;

        const change = calculateChange(value, pastValue);
        const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";
        const explaination = dataset1[header]?.[attribute]?.explanation;
        const sources = dataset1[header]?.[attribute]?.sources;

        return {
          value,
          change,
          trend,
          explaination,
          sources,
        };
      }),
    }));

  // Prepare rows for Core Attributes table

  const attributes = [
    ...new Set(
      Object.values(dataset5).flatMap((brandObj) => Object.keys(brandObj))
    ),
  ];
  const categoryDimensionsRows = attributes?.map((attribute) => ({
    attribute,
    values: headers.slice(0).map((header) => {
      const value = dataset5[header]?.[attribute]?.avgValue ?? 0;

      // For LLM tab, compare with same provider's past LLM
      const pastLlmKey = isLlmTab ? findPastLlmByProvider(header, pastDataset5) : header;
      const pastValue = pastDataset5[pastLlmKey]?.[attribute]?.avgValue;

      const change = calculateChange(value, pastValue);
      const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";
      const explaination = dataset5[header]?.[attribute]?.explanation;
      const sources = dataset5[header]?.[attribute]?.sources;
      const isNa = value ? false : true;

      return {
        value,
        change,
        trend,
        explaination,
        isNa,
        sources,
      };
    }),
  }));

  // // Prepare rows for RBI table based on dataset2
  // const rbiRows = Object.keys(dataset2).map((brand) => ({
  //   attribute: brand,
  //   values: headers.slice(0).map((header) => {
  //     const value = dataset2[brand]?.[header] ?? 0;
  //     const pastValue = pastDataset2[brand]?.[header];
  //     const change = calculateChange(value, pastValue);
  //     const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";

  //     return {
  //       value,
  //       change,
  //       trend,
  //     };
  //   }),
  // }));

  // // Prepare rows for RBR table based on dataset3
  // const rbrRows = Object.keys(dataset3).map((brand) => ({
  //   attribute: brand,
  //   values: headers.slice(0).map((header) => {
  //     const value = dataset3[brand]?.[header] ?? 0;
  //     const pastValue = pastDataset3[brand]?.[header];
  //     const change = calculateChange(value, pastValue);
  //     const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";

  //     return {
  //       value,
  //       change,
  //       trend,
  //     };
  //   }),
  // }));

  // // Prepare rows for RBF table based on dataset4
  // const rbfRows = Object.keys(dataset4).map((brand) => ({
  //   attribute: brand,
  //   values: headers.slice(0).map((header) => {
  //     const value = dataset4[brand]?.[header] ?? 0;
  //     const pastValue = pastDataset4[brand]?.[header];
  //     const change = calculateChange(value, pastValue);
  //     const trend = change > 0 ? "up" : change < 0 ? "down" : "neutral";

  //     return {
  //       value,
  //       change,
  //       trend,
  //     };
  //   }),
  // }));

  // // Prepare rows for rank table based on dataset3
  // Reusable row builder
  const buildRows = (
    dataset,
    headers,
    isMarketOverview,
    competitiveOverview,
    selectedCategory
  ) => {
    const useSelectedCategory = isMarketOverview?.length > 0 || personaOverview?.length > 0;
    const useCompetitive = competitiveOverview?.length > 0;
    return Object.entries(dataset).map(([brand, brandData]) => ({
      attribute: brand,
      values: headers.map((header) => {
        const competitiveHeader = header.toLowerCase().replace(/\s+/g, "");
        let value = 0;

        if (useSelectedCategory) {
          value = brandData?.[selectedCategory]?.[header] ?? 0;
        } else if (useCompetitive) {
          value = brandData?.[competitiveHeader] ?? 0;
        } else {
          value = brandData?.[header] ?? 0;
        }

        return { value };
      }),
    }));
  };

  // Usage
  const rbrRows = buildRows(
    dataset3,
    headers,
    isMarketOverview,
    competitiveOverview,
    selectedCatgory
  );
  const rbfRows = buildRows(
    dataset4,
    headers,
    isMarketOverview,
    competitiveOverview,
    selectedCatgory
  );

  // merged rbi rows
  const mergedRbiRows = Object.keys(dataset2).map((brand) => ({
    attribute: brand,
    values: headers.map((header) => {
      // For LLM tab, compare with same provider's past LLM
      // For RBI data structure [brand][llmName], pass brand-specific data
      const pastLlmKey = isLlmTab ? findPastLlmByProvider(header, pastDataset2[brand] || {}) : header;

      const rbiValue = dataset2[brand]?.[header] ?? 0;
      const pastRbiValue = pastDataset2[brand]?.[pastLlmKey];
      const rbiChange = calculateChange(rbiValue, pastRbiValue);
      const rbiTrend =
        rbiChange > 0 ? "up" : rbiChange < 0 ? "down" : "neutral";

      const rbrValue = dataset3[brand]?.[header] ?? 0;
      const pastRbrValue = pastDataset3[brand]?.[pastLlmKey];
      const rbrChange = calculateChange(rbrValue, pastRbrValue);
      const rbrTrend =
        rbrChange > 0 ? "up" : rbrChange < 0 ? "down" : "neutral";

      const rbfValue = dataset4[brand]?.[header] ?? 0;
      const pastRbfValue = pastDataset4[brand]?.[pastLlmKey];
      const rbfChange = calculateChange(rbfValue, pastRbfValue);
      const rbfTrend =
        rbfChange > 0 ? "up" : rbfChange < 0 ? "down" : "neutral";

      return {
        rbivalue: rbiValue,
        rbichange: rbiChange,
        rbitrend: rbiTrend,
        rbrvalue: rbrValue,
        rbrchange: rbrChange,
        rbrtrend: rbrTrend,
        rbfvalue: rbfValue,
        rbfchange: rbfChange,
        rbftrend: rbfTrend,
      };
    }),
  }));

  // const secCategoryMergedRbiRows = {};

  // Object.keys(secCategoryIndexScore || {}).forEach((categoryId) => {
  //   secCategoryMergedRbiRows[categoryId] = Object.keys(
  //     secCategoryIndexScore[categoryId] || {}
  //   ).map((brand) => ({
  //     attribute: brand,
  //     values: headers.map((header) => {
  //       const rbiValue =
  //         secCategoryIndexScore[categoryId]?.[brand]?.[header] ?? 0;
  //       const pastRbiValue =
  //         pastsecCategoryBrandIndex?.indexScore?.[categoryId]?.[brand]?.[
  //           header
  //         ];
  //       const rbiChange = calculateChange(rbiValue, pastRbiValue);
  //       const rbiTrend =
  //         rbiChange > 0 ? "up" : rbiChange < 0 ? "down" : "neutral";

  //       const rbrValue =
  //         secCategoryreachScore[categoryId]?.[brand]?.[header] ?? 0;
  //       const pastRbrValue =
  //         pastsecCategoryBrandIndex?.rank[categoryId]?.[brand]?.[header];
  //       const rbrChange = calculateChange(rbrValue, pastRbrValue);
  //       const rbrTrend =
  //         rbrChange > 0 ? "up" : rbrChange < 0 ? "down" : "neutral";

  //       const rbfValue =
  //         secCategoryfavScore[categoryId]?.[brand]?.[header] ?? 0;
  //       const pastRbfValue =
  //         pastsecCategoryBrandIndex?.fav[categoryId]?.[brand]?.[header];
  //       const rbfChange = calculateChange(rbfValue, pastRbfValue);
  //       const rbfTrend =
  //         rbfChange > 0 ? "up" : rbfChange < 0 ? "down" : "neutral";

  //       return {
  //         rbivalue: rbiValue,
  //         rbichange: rbiChange,
  //         rbitrend: rbiTrend,
  //         rbrvalue: rbrValue,
  //         rbrchange: rbrChange,
  //         rbrtrend: rbrTrend,
  //         rbfvalue: rbfValue,
  //         rbfchange: rbfChange,
  //         rbftrend: rbfTrend,
  //       };
  //     }),
  //   }));
  // });

  // const secCategoryIndexScoreRows = Object.keys(
  //   secCategoryMergedRbiRows || {}
  // ).map((categoryId) => ({
  //   title: `RBI-${categoryId}`, // you can customize this if needed based on categoryId
  //   id: `rbi_pc_${categoryId}`, // appending categoryId to make id unique
  //   rows:
  //     sortByAttributeMatch(
  //       secCategoryMergedRbiRows[categoryId],
  //       brandProduct
  //     ) || [],
  //   type: "RBI",
  //   category: categoryId,
  // }));
  const rbiBlock = {
    title: "RBI",
    id: "rbi_pc",
    rows: sortByAttributeMatch(mergedRbiRows, brandProduct),
    type: "RBI",
  };

  let otherMetrics = [];

  if (isBrandAuditMarketPage && isOverview) {
    // Brand Overview
    otherMetrics = [
      { title: "Rank", id: "rank_id", rows: rbrRows },
      { title: "Favorability", id: "fav_id", rows: rbfRows },
    ];
  } else if (isBrandAuditMarketPage) {
    // Market Page
    otherMetrics = [rbiBlock];
  } else {
    // (non-market) → RBI + Brand/Pillars + Purchase Criteria
    otherMetrics = [
      rbiBlock,
      {
        title: isProductTaxonomy ? "Product Dimensions" : `${ENTITY_LABELS.Brand} Pillars`,
        id: "bra_id",
        rows: coreAttributesRows,
      },
      {
        title: ENTITY_LABELS.PurchaseCriteria,
        id: "pur_id",
        rows: categoryDimensionsRows,
      },
    ];
  }

  return {
    headers,
    tables: [...otherMetrics],
  };
}

function sortByAttributeMatch(arr, targetAttribute) {
  if (!arr || arr.length === 0) return arr;
  return arr.sort((a, b) => {
    if (a.attribute === targetAttribute) return -1;
    if (b.attribute === targetAttribute) return 1;
    return 0;
  });
}

export function transformDataForMultibarChart(data, isSource) {
  // Initialize an object to hold the aggregated data

  if (data) {
    const transformedData = {};

    // Iterate over each brand in the data
    for (const [brand, attributes] of Object.entries(data)) {
      // Iterate over each attribute in the brand
      for (const [attribute, score] of Object.entries(attributes)) {
        // If the attribute is not yet in the transformed data, add it
        if (!transformedData[attribute]) {
          transformedData[attribute] = { name: attribute };
        }
        // Add the score for the current brand
        transformedData[attribute][brand] = score;
      }
    }

    const attributeAverages = Object.values(transformedData).map((attrObj) => {
      const { name, ...brands } = attrObj;
      const totalSignal = Object.values(brands).reduce(
        (sum, val) => sum + val,
        0
      );
      const averageSignal = totalSignal / Object.values(brands).length;
      return { name, averageSignal, ...brands };
    });

    // Sort attributes by average signal strength in descending order
    const sortedAttributes = attributeAverages.sort(
      (a, b) => b.averageSignal - a.averageSignal
    );

    // Return only the top 10 attributes, removing the averageSignal key
    return isSource
      ? sortedAttributes.map(({ averageSignal, ...rest }) => rest)
      : sortedAttributes.slice(0, 10).map(({ averageSignal, ...rest }) => rest);
  }
}

export function transformSourceDataForMultibarChart(data) {
  if (!data) return;

  const transformedData = {};

  // Iterate over each brand (LLM) in the data
  for (const [brand, domains] of Object.entries(data)) {
    // Each "attribute" is now a domain object
    for (const [domain, domainObj] of Object.entries(domains)) {
      const count = domainObj.source_alias_count || 0;
      const aliases = domainObj.aliases || [];

      if (!transformedData[domain]) {
        transformedData[domain] = { name: domain };
      }

      // store a helper value for calculation, and the final object
      transformedData[domain][brand] = {
        count,
        aliases,
        // helper used only for computing averages
        _calc: count,
      };
    }
  }

  // compute averages using the helper counts
  const attributeAverages = Object.values(transformedData).map((attrObj) => {
    const { name, ...brands } = attrObj;

    const totalSignal = Object.values(brands).reduce(
      (sum, val) => sum + (val._calc ?? 0),
      0
    );

    const averageSignal = totalSignal / Object.values(brands).length;

    // strip the helper before returning
    const cleanedBrands = {};
    for (const [b, v] of Object.entries(brands)) {
      const { _calc, ...rest } = v;
      cleanedBrands[b] = rest;
    }

    return { name, averageSignal, ...cleanedBrands };
  });

  // sort by average signal (count based)
  const sortedAttributes = attributeAverages.sort(
    (a, b) => b.averageSignal - a.averageSignal
  );

  console.log(
    "testingsource==>",
    sortedAttributes.map(({ averageSignal, ...rest }) => rest)
  );

  // remove averageSignal key in final output
  return sortedAttributes.map(({ averageSignal, ...rest }) => rest);
}

export function extractDomain(input) {
  // Check if the input is already in the desired format
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) {
    return input;
  }

  try {
    // If it's not a full URL, prepend 'http://' to make it valid for URL parsing
    if (!/^https?:\/\//i.test(input)) {
      input = "http://" + input;
    }

    // Create a URL object
    const urlObject = new URL(input);

    // Get the hostname (e.g., "in.puma.com")
    let hostname = urlObject.hostname;

    // Split the hostname by dots
    const parts = hostname.split(".");

    // If we have more than 2 parts, take the last two
    if (parts.length > 2) {
      hostname = parts.slice(-2).join(".");
    }

    return hostname;
  } catch (error) {
    console.error("Invalid input:", error.message);
    return null;
  }
}

export function splitObjectByKey(inputNumber, obj) {
  const filteredObj = {};
  const remainingObj = {};

  Object.keys(obj).forEach((key) => {
    if (Number(key) >= Number(inputNumber)) {
      filteredObj[key] = obj[key]; // Add to filtered object
    } else {
      remainingObj[key] = obj[key]; // Add to remaining object
    }
  });

  return { filteredObj, remainingObj };
}

export function coreAttributesWithDeltaScores(dataset1, dataset2) {
  return dataset1?.map((item1, index) => {
    const item2 = dataset2.find((item) => item?.attribute === item1?.attribute);

    if (item2) {
      const deltaMainAvgScore =
        Number(item1?.mainAvgScore) - Number(item2?.mainAvgScore);
      const deltaAvgScore = Number(item1?.avgScore) - Number(item2?.avgScore);

      return {
        ...item1,
        deltaMainAvgScore: deltaMainAvgScore.toFixed(0), // Adding deltaMainAvgScore
        deltaAvgScore: deltaAvgScore.toFixed(0), // Adding deltaAvgScore
      };
    }

    return item1; // If the attribute is not found in dataset2, return the original item
  });
}

// export function splitByDayAndMonth(
//   inputDay,
//   inputMonth,
//   inputYear,
//   data,
//   isCurrent
// ) {
//   let filteredData = [];
//   let remainingData = [];

//   if (isCurrent) {
//     // Step 1: Extract unique dates from the data
//     const uniqueDates = [
//       ...new Set(data.map((item) => `${item.day}-${item.month}-${item.year}`)),
//     ]
//       .map((dateStr) => {
//         const [day, month, year] = dateStr.split("-");
//         return { day: Number(day), month: Number(month), year: Number(year) };
//       })
//       .sort((a, b) => {
//         // Sort dates to get the latest first
//         if (a.year === b.year) {
//           if (a.month === b.month) {
//             return b.day - a.day; // Sort by day if the month and year are the same
//           }
//           return b.month - a.month; // Otherwise sort by month
//         }
//         return b.year - a.year; // Otherwise sort by year
//       });

//     if (uniqueDates.length > 0) {
//       const latestDate = uniqueDates[0]; // The most recent date
//       const previousDate = uniqueDates[1] || null; // The second most recent date, if it exists

//       // Step 2: Split the data into filteredData and remainingData
//       data.forEach((item) => {
//         const { day, month, year } = item;

//         if (
//           Number(day) === latestDate.day &&
//           Number(month) === latestDate.month &&
//           Number(year) === latestDate.year
//         ) {
//           filteredData.push(item);
//         } else if (
//           previousDate &&
//           Number(day) === previousDate.day &&
//           Number(month) === previousDate.month &&
//           Number(year) === previousDate.year
//         ) {
//           remainingData.push(item);
//         }
//       });
//     }
//   } else {
//     // Logic for isCurrent = false
//     data.forEach((item) => {
//       const { day, month, year } = item;

//       if (
//         Number(year) > Number(inputYear) ||
//         (Number(year) === Number(inputYear) &&
//           (Number(month) > Number(inputMonth) ||
//             (Number(month) === Number(inputMonth) &&
//               Number(day) >= Number(inputDay))))
//       ) {
//         filteredData.push(item);
//       } else {
//         remainingData.push(item);
//       }
//     });
//   }

//   return { filteredData, remainingData };
// }

export function splitByDayAndMonth(
  inputDay,
  inputMonth,
  inputYear,
  data,
  isCurrent,
  isCustom
) {
  let filteredData = [];
  let remainingData = [];

  // helper: extract unique dates
  const getUniqueDates = () =>
    [
      ...new Set(data.map((item) => `${item.day}-${item.month}-${item.year}`)),
    ].map((dateStr) => {
      const [day, month, year] = dateStr.split("-");
      return { day: +day, month: +month, year: +year };
    });

  // helper: sort dates latest first
  const sortDates = (arr) =>
    arr.sort((a, b) =>
      a.year === b.year
        ? a.month === b.month
          ? b.day - a.day
          : b.month - a.month
        : b.year - a.year
    );

  // helper: split data into filtered & remaining
  const splitByDates = (latestDate, previousDate) => {
    data.forEach(({ day, month, year, ...rest }) => {
      if (
        latestDate &&
        +day === latestDate.day &&
        +month === latestDate.month &&
        +year === latestDate.year
      ) {
        filteredData.push({ day, month, year, ...rest });
      } else if (
        previousDate &&
        +day === previousDate.day &&
        +month === previousDate.month &&
        +year === previousDate.year
      ) {
        remainingData.push({ day, month, year, ...rest });
      }
    });
  };

  if (isCustom) {
    let uniqueDates = getUniqueDates();
    let latestDate = null,
      previousDate = null;

    if (uniqueDates.length === 1) {
      const onlyDate = uniqueDates[0];
      if (
        onlyDate.day === +inputDay &&
        onlyDate.month === +inputMonth &&
        onlyDate.year === +inputYear
      ) {
        latestDate = onlyDate;
      } else {
        previousDate = onlyDate;
      }
    } else {
      uniqueDates = sortDates(uniqueDates);
      latestDate = uniqueDates[0] || null;
      previousDate = uniqueDates[1] || null;
    }

    splitByDates(latestDate, previousDate);
  } else if (isCurrent) {
    const uniqueDates = sortDates(getUniqueDates());
    if (uniqueDates.length > 0) {
      splitByDates(uniqueDates[0], uniqueDates[1] || null);
    }
  } else {
    data.forEach(({ day, month, year, ...rest }) => {
      if (
        +year > +inputYear ||
        (+year === +inputYear &&
          (+month > +inputMonth ||
            (+month === +inputMonth && +day >= +inputDay)))
      ) {
        filteredData.push({ day, month, year, ...rest });
      } else {
        remainingData.push({ day, month, year, ...rest });
      }
    });
  }

  return { filteredData, remainingData };
}

// export function splitDataObjByDayAndMonth(
//   inputDay,
//   inputMonth,
//   inputYear,
//   dataObj,
//   isCurrent
// ) {
//   const filteredData = {};
//   const remainingData = {};

//   if (dataObj) {
//     Object.keys(dataObj).forEach((key) => {
//       const filteredArray = [];
//       const remainingArray = [];

//       if (isCurrent) {
//         // Step 1: Extract unique dates from the data for each key
//         const uniqueDates = [
//           ...new Set(
//             dataObj[key].map((item) => `${item.day}-${item.month}-${item.year}`)
//           ),
//         ]
//           .map((dateStr) => {
//             const [day, month, year] = dateStr.split("-");
//             return {
//               day: Number(day),
//               month: Number(month),
//               year: Number(year),
//             };
//           })
//           .sort((a, b) => {
//             // Sort dates to get the latest first
//             if (a.year === b.year) {
//               if (a.month === b.month) {
//                 return b.day - a.day; // Sort by day if the month and year are the same
//               }
//               return b.month - a.month; // Otherwise sort by month
//             }
//             return b.year - a.year; // Otherwise sort by year
//           });

//         if (uniqueDates.length > 0) {
//           const latestDate = uniqueDates[0];
//           const previousDate = uniqueDates[1] || null;

//           // Step 2: Split the data into filteredArray and remainingArray
//           dataObj[key].forEach((item) => {
//             const { day, month, year } = item;

//             if (
//               Number(day) === latestDate.day &&
//               Number(month) === latestDate.month &&
//               Number(year) === latestDate.year
//             ) {
//               filteredArray.push(item);
//             } else if (
//               previousDate &&
//               Number(day) === previousDate.day &&
//               Number(month) === previousDate.month &&
//               Number(year) === previousDate.year
//             ) {
//               remainingArray.push(item);
//             }
//           });
//         }
//       } else {
//         // Logic for isCurrent = false
//         dataObj[key].forEach((item) => {
//           const { day, month, year } = item;

//           if (
//             Number(year) > Number(inputYear) ||
//             (Number(year) === Number(inputYear) &&
//               (Number(month) > Number(inputMonth) ||
//                 (Number(month) === Number(inputMonth) &&
//                   Number(day) >= Number(inputDay))))
//           ) {
//             filteredArray.push(item);
//           } else {
//             remainingArray.push(item);
//           }
//         });
//       }

//       filteredData[key] = filteredArray;
//       remainingData[key] = remainingArray;
//     });
//   }

//   return { filteredData, remainingData };
// }

export function splitDataObjByDayAndMonth(
  inputDay,
  inputMonth,
  inputYear,
  dataObj,
  isCurrent,
  isCustom
) {
  const filteredData = {};
  const remainingData = {};

  if (dataObj) {
    Object.keys(dataObj).forEach((key) => {
      const filteredArray = [];
      const remainingArray = [];

      if (isCustom) {
        // Step 1: Extract unique dates from the data for each key
        let uniqueDates = [
          ...new Set(
            dataObj[key].map((item) => `${item.day}-${item.month}-${item.year}`)
          ),
        ].map((dateStr) => {
          const [day, month, year] = dateStr.split("-");
          return {
            day: Number(day),
            month: Number(month),
            year: Number(year),
          };
        });

        let latestDate = null;
        let previousDate = null;

        if (uniqueDates.length === 1) {
          // Special handling when only one date exists
          const onlyDate = uniqueDates[0];
          if (
            onlyDate.day === Number(inputDay) &&
            onlyDate.month === Number(inputMonth) &&
            onlyDate.year === Number(inputYear)
          ) {
            latestDate = onlyDate; // match input → latest
          } else {
            previousDate = onlyDate; // no match → previous
          }
        } else {
          // If more than one date → behave like isCurrent
          uniqueDates.sort((a, b) => {
            if (a.year === b.year) {
              if (a.month === b.month) {
                return b.day - a.day;
              }
              return b.month - a.month;
            }
            return b.year - a.year;
          });

          latestDate = uniqueDates[0] || null;
          previousDate = uniqueDates[1] || null;
        }

        // Step 2: Split the data into filteredArray and remainingArray
        dataObj[key].forEach((item) => {
          const { day, month, year } = item;

          if (
            latestDate &&
            Number(day) === latestDate.day &&
            Number(month) === latestDate.month &&
            Number(year) === latestDate.year
          ) {
            filteredArray.push(item);
          } else if (
            previousDate &&
            Number(day) === previousDate.day &&
            Number(month) === previousDate.month &&
            Number(year) === previousDate.year
          ) {
            remainingArray.push(item);
          }
        });
      } else if (isCurrent) {
        // ✅ your original isCurrent block untouched
        const uniqueDates = [
          ...new Set(
            dataObj[key].map((item) => `${item.day}-${item.month}-${item.year}`)
          ),
        ]
          .map((dateStr) => {
            const [day, month, year] = dateStr.split("-");
            return {
              day: Number(day),
              month: Number(month),
              year: Number(year),
            };
          })
          .sort((a, b) => {
            if (a.year === b.year) {
              if (a.month === b.month) {
                return b.day - a.day;
              }
              return b.month - a.month;
            }
            return b.year - a.year;
          });

        if (uniqueDates.length > 0) {
          const latestDate = uniqueDates[0];
          const previousDate = uniqueDates[1] || null;

          dataObj[key].forEach((item) => {
            const { day, month, year } = item;

            if (
              Number(day) === latestDate.day &&
              Number(month) === latestDate.month &&
              Number(year) === latestDate.year
            ) {
              filteredArray.push(item);
            } else if (
              previousDate &&
              Number(day) === previousDate.day &&
              Number(month) === previousDate.month &&
              Number(year) === previousDate.year
            ) {
              remainingArray.push(item);
            }
          });
        }
      } else {
        // ✅ your original else block untouched
        dataObj[key].forEach((item) => {
          const { day, month, year } = item;

          if (
            Number(year) > Number(inputYear) ||
            (Number(year) === Number(inputYear) &&
              (Number(month) > Number(inputMonth) ||
                (Number(month) === Number(inputMonth) &&
                  Number(day) >= Number(inputDay))))
          ) {
            filteredArray.push(item);
          } else {
            remainingArray.push(item);
          }
        });
      }

      filteredData[key] = filteredArray;
      remainingData[key] = remainingArray;
    });
  }

  return { filteredData, remainingData };
}

export function transformAttCategoryData(data, option) {
  let positiveResult = [];
  let negativeResult = [];

  if (data && data.length) {
    const positiveData = data?.filter((obj) => obj?.is_positive);
    const negativeData = data?.filter((obj) => !obj?.is_positive);

    const processData = (item, resultArray) => {
      const categoryName =
        option === "Product"
          ? item?.attribute?.BrandProductAttCategory?.attribute_category
            .category_name
          : item?.attribute?.BrandAttCategory?.attribute_category.category_name;
      const signalStrength = Number(
        option === "Product"
          ? item?.attribute?.BrandProductAttCategory?.signal_strength
          : item?.attribute?.BrandAttCategory?.signal_strength
      );
      const attributeName = item?.attribute?.name;
      const date = item?.day; // The date field

      // Find if the category already exists in the result
      const existingCategory = resultArray.find(
        (cat) => cat.name === categoryName
      );

      if (existingCategory) {
        // Add the attribute name to altNames array if it's not already there
        if (!existingCategory.altNames.includes(attributeName)) {
          existingCategory.altNames.push(attributeName);
        }

        // Check if the date already exists in the category's date set
        if (!existingCategory.dates[date]) {
          // If the date doesn't exist, initialize it with the current signal strength
          existingCategory.dates[date] = signalStrength;
        } else {
          // If the date exists, you can choose to average or replace the value
          existingCategory.dates[date] =
            (existingCategory.dates[date] + signalStrength) / 2;
        }

        // Recalculate the average signal strength based on the number of dates
        const totalSignalStrength = Object.values(
          existingCategory.dates
        ).reduce((acc, strength) => acc + strength, 0);
        const totalDates = Object.keys(existingCategory.dates).length;
        existingCategory.value = (totalSignalStrength / totalDates).toFixed(0);
      } else {
        // Add new entry if category doesn't exist
        resultArray.push({
          name: categoryName,
          value: signalStrength.toFixed(0),
          altNames: [attributeName],
          dates: {
            [date]: signalStrength, // Initialize with the first signal strength for this date
          },
        });
      }
    };

    positiveData.forEach((item) => {
      processData(item, positiveResult);
    });

    negativeData.forEach((item) => {
      processData(item, negativeResult);
    });
  }

  return { positiveResult, negativeResult };
}

export const getAttDataWithDelta = (currentData, pastData) => {
  if (currentData) {
    const updatedCurrentData = currentData?.map((currentItem) => {
      // Find corresponding past item by name
      const pastItem = pastData.find(
        (pastItem) => pastItem.name === currentItem.name
      );

      // Calculate delta if the pastItem is found
      const delta = pastItem
        ? Number(currentItem.value) - Number(pastItem.value)
        : null;

      // Return the updated current item with the delta property
      return {
        ...currentItem,
        delta: delta,
      };
    });

    return updatedCurrentData;
  }
};

export const getBaseCoreAttributes = (data, isCurrent) => {
  if (data) {
    let maxKeyCount = Math.max(
      ...Object.values(data).map((obj) => Object.keys(obj).length)
    );
    let filteredEntries = Object.entries(data).filter(
      ([key, obj]) => Object.keys(obj).length === maxKeyCount
    );
    let lowestKeyEntry = filteredEntries.reduce((target, current) => {
      if (isCurrent) {
        return +current[0] > +target[0] ? current : target;
      }
      return +current[0] < +target[0] ? current : target;
    });

    const baseObj = lowestKeyEntry[1];

    return baseObj;
  }
};

export const getBaseCompBrandIndexScore = (data, isCurrent, option) => {
  const result = {};
  if (data && data.length) {
    data.forEach((item) => {
      if (option === "Product") {
        if (
          !(item?.comp_brand_product_id in result) ||
          (isCurrent
            ? +item?.day > result[item?.comp_brand_product_id]?.day
            : +item?.day > result[item?.comp_brand_product_id]?.day)
        ) {
          result[item?.comp_brand_product_id] = item;
        }
      } else {
        if (
          !(item?.comp_brand_id in result) ||
          (isCurrent
            ? +item?.day > result[item?.comp_brand_id]?.day
            : +item?.day > result[item?.comp_brand_id]?.day)
        ) {
          result[item?.comp_brand_id] = item;
        }
      }
    });

    const filteredData = Object.values(result);
    return filteredData;
  }
};

export function addBaseValueToIndexScoreData(data) {
  let maxCount = 0;
  let candidates = [];
  for (const key in data) {
    let totalProperties = 0;

    for (const childKey in data[key]) {
      totalProperties += Object.keys(data[key][childKey]).length;
    }

    // If the current key has more properties than the max count, update maxCount
    if (totalProperties > maxCount) {
      maxCount = totalProperties;
      candidates = [key]; // Reset the candidates array with the current key
    }
    // If the current key has the same number of properties as the max count, add it to the candidates array
    else if (totalProperties === maxCount) {
      candidates.push(key);
    }
  }

  // Find the lowest key among the candidates
  const lowestKey = Math.min(...candidates.map(Number));

  const target = { [lowestKey]: data[lowestKey] };

  return target;
}

export async function validateLinks(sources) {
  try {
    let promiseArr = [];

    for (let source of sources) {
      promiseArr.push(
        axios.post(
          "https://brandluminaire.eastus.cloudapp.azure.com/api/checkWebLink",
          { link: source },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );
    }

    const result = await Promise.all(promiseArr);
    const jsonResults = result.map((res) => res.data);
    return jsonResults
      .filter((res) => res.status === "success")
      .map((res) => res.link);
  } catch (error) {
    console.error("An error occurred:", error);
    throw error;
  }
}

export const combineData = (data1, data2) => {
  const result = {};

  // Iterate over each brand in data1
  Object.keys(data1).forEach((brand) => {
    result[brand] = {};

    // Iterate over all attributes for each brand
    Object.keys(data1[brand]).forEach((attribute) => {
      const value1 = parseFloat(data1[brand][attribute] || 0);
      const value2 = parseFloat((data2[brand] && data2[brand][attribute]) || 0);

      // Sum the values for the attribute
      result[brand][attribute] = (value1 + value2).toFixed(10); // Adjust precision if necessary
    });
  });

  return result;
};

export const combineObjectValues = (data1, data2) => {
  const result = {};

  // Iterate over all brands in data1
  Object.keys(data1).forEach((brand) => {
    const value1 = parseFloat(data1[brand] || 0);
    const value2 = parseFloat(data2[brand] || 0);

    // Sum the values for the brand
    result[brand] = (value1 + value2).toFixed(0); // Adjust precision if needed
  });

  return result;
};

export function transformCustomerProductIndexData(data) {
  const brandProductMap = {};

  // Iterate through the data to group by brand and product, and aggregate scores by LLM
  data.forEach((item) => {
    const brandId =
      item.customer_brand_product_persona.custProduct.product.brand_id;
    const productId =
      item.customer_brand_product_persona.custProduct.product.id;
    const customerProductId =
      item.customer_brand_product_persona.custProduct.id;
    const productName =
      item.customer_brand_product_persona.custProduct.product.name;
    const productDescription =
      item.customer_brand_product_persona.custProduct.product.description;
    const llmName = item.customer_llm.llm.name;

    if (!brandProductMap[brandId]) {
      brandProductMap[brandId] = {
        brandId,
        products: {},
      };
    }

    if (!brandProductMap[brandId].products[productId]) {
      brandProductMap[brandId].products[productId] = {
        customerProductId,
        productId,
        productName,
        productDescription,
        llms: {},
        totalIndexScore: 0,
        totalFavorabilityScore: 0,
        totalReachScore: 0,
        llmCount: 0, // Track total LLMs processed per product
      };
    }

    // Initialize LLM object if not present
    if (!brandProductMap[brandId].products[productId].llms[llmName]) {
      brandProductMap[brandId].products[productId].llms[llmName] = {
        totalIndexScore: 0,
        totalFavorabilityScore: 0,
        totalReachScore: 0,
        count: 0, // Track number of records for this LLM
      };
    }

    // Accumulate scores for the LLM, ensuring values are parsed as numbers
    brandProductMap[brandId].products[productId].llms[
      llmName
    ].totalIndexScore += parseFloat(item.avg_brand_index_score) || 0;
    brandProductMap[brandId].products[productId].llms[
      llmName
    ].totalFavorabilityScore +=
      parseFloat(item.avg_brand_favorability_score) || 0;
    brandProductMap[brandId].products[productId].llms[
      llmName
    ].totalReachScore += parseFloat(item.avg_brand_reach_score) || 0;
    brandProductMap[brandId].products[productId].llms[llmName].count += 1;

    // Aggregate total scores for averaging across LLMs
    brandProductMap[brandId].products[productId].totalIndexScore +=
      parseFloat(item.avg_brand_index_score) || 0;
    brandProductMap[brandId].products[productId].totalFavorabilityScore +=
      parseFloat(item.avg_brand_favorability_score) || 0;
    brandProductMap[brandId].products[productId].totalReachScore +=
      parseFloat(item.avg_brand_reach_score) || 0;
    brandProductMap[brandId].products[productId].llmCount += 1;
  });

  // Prepare final data structure for rendering
  const finalData = Object.values(brandProductMap).map((brand) => {
    const products = Object.values(brand.products).map((product) => {
      // Compute average scores across LLMs, ensuring the division results in a valid number
      const avgIndexScore = product.llmCount
        ? (product.totalIndexScore / product.llmCount).toFixed(0)
        : "0.00";
      const avgFavorabilityScore = product.llmCount
        ? (product.totalFavorabilityScore / product.llmCount).toFixed(0)
        : "0.00";
      const avgReachScore = product.llmCount
        ? (product.totalReachScore / product.llmCount).toFixed(0)
        : "0.00";

      // Compute the average score for each LLM
      const llms = Object.entries(product.llms).reduce(
        (acc, [llmName, llmData]) => {
          const avgIndexScore = llmData.count
            ? (llmData.totalIndexScore / llmData.count).toFixed(0)
            : "0.00";
          const avgFavorabilityScore = llmData.count
            ? (llmData.totalFavorabilityScore / llmData.count).toFixed(0)
            : "0.00";
          const avgReachScore = llmData.count
            ? (llmData.totalReachScore / llmData.count).toFixed(0)
            : "0.00";

          acc[llmName] = {
            avg_brand_index_score: avgIndexScore,
            avg_brand_favorability_score: avgFavorabilityScore,
            avg_brand_reach_score: avgReachScore,
          };
          return acc;
        },
        {}
      );

      return {
        customerProductId: product.customerProductId,
        productId: product.productId,
        productName: product.productName,
        productDescription: product.productDescription,
        avgScores: {
          avg_brand_index_score: avgIndexScore,
          avg_brand_favorability_score: avgFavorabilityScore,
          avg_brand_reach_score: avgReachScore,
        },
        llms,
      };
    });

    return {
      brandId: brand.brandId,
      products,
    };
  });

  return finalData;
}

export function transformCustomerIndexData(data) {
  const brandMap = {};

  // Iterate through the data to group by brand and aggregate scores by LLM
  data.forEach((item) => {
    const brandId = item.customer_brand_persona.customer_brand.brand.id;
    const customerBrandId = item.customer_brand_persona.customer_brand.id;
    const brandName = item.customer_brand_persona.customer_brand.brand.name;
    const brandDescription =
      item.customer_brand_persona.customer_brand.brand.description;
    const brandDomain =
      item.customer_brand_persona.customer_brand.brand.domain_url;
    const llmName = item.customer_llm.llm.name;

    if (!brandMap[brandId]) {
      brandMap[brandId] = {
        customerBrandId,
        brandId,
        brandName,
        brandDescription,
        brandDomain,
        llms: {},
        totalIndexScore: 0,
        totalFavorabilityScore: 0,
        totalReachScore: 0,
        llmCount: 0, // Track total LLMs processed
      };
    }

    // Initialize LLM object if not present
    if (!brandMap[brandId].llms[llmName]) {
      brandMap[brandId].llms[llmName] = {
        totalIndexScore: 0,
        totalFavorabilityScore: 0,
        totalReachScore: 0,
        count: 0, // Track number of records for this LLM
      };
    }

    // Accumulate scores for the LLM, ensuring values are parsed as numbers
    brandMap[brandId].llms[llmName].totalIndexScore +=
      parseFloat(item.avg_brand_index_score) || 0;
    brandMap[brandId].llms[llmName].totalFavorabilityScore +=
      parseFloat(item.avg_brand_favorability_score) || 0;
    brandMap[brandId].llms[llmName].totalReachScore +=
      parseFloat(item.avg_brand_reach_score) || 0;
    brandMap[brandId].llms[llmName].count += 1;

    // Aggregate total scores across all LLMs for this brand
    brandMap[brandId].totalIndexScore +=
      parseFloat(item.avg_brand_index_score) || 0;
    brandMap[brandId].totalFavorabilityScore +=
      parseFloat(item.avg_brand_favorability_score) || 0;
    brandMap[brandId].totalReachScore +=
      parseFloat(item.avg_brand_reach_score) || 0;
    brandMap[brandId].llmCount += 1;
  });

  // Prepare final data structure for rendering
  const finalData = Object.values(brandMap).map((brand) => {
    // Compute average scores across LLMs, ensuring the division results in a valid number
    const avgIndexScore = brand.llmCount
      ? (brand.totalIndexScore / brand.llmCount).toFixed(0)
      : "0.00";
    const avgFavorabilityScore = brand.llmCount
      ? (brand.totalFavorabilityScore / brand.llmCount).toFixed(0)
      : "0.00";
    const avgReachScore = brand.llmCount
      ? (brand.totalReachScore / brand.llmCount).toFixed(0)
      : "0.00";

    // Compute the average score for each LLM
    const llms = Object.entries(brand.llms).reduce(
      (acc, [llmName, llmData]) => {
        const avgIndexScore = llmData.count
          ? (llmData.totalIndexScore / llmData.count).toFixed(0)
          : "0.00";
        const avgFavorabilityScore = llmData.count
          ? (llmData.totalFavorabilityScore / llmData.count).toFixed(0)
          : "0.00";
        const avgReachScore = llmData.count
          ? (llmData.totalReachScore / llmData.count).toFixed(0)
          : "0.00";

        acc[llmName] = {
          avg_brand_index_score: avgIndexScore,
          avg_brand_favorability_score: avgFavorabilityScore,
          avg_brand_reach_score: avgReachScore,
        };
        return acc;
      },
      {}
    );

    return {
      customerBrandId: brand.customerBrandId,
      brandId: brand.brandId,
      brandName: brand.brandName,
      brandDomain: brand.brandDomain,
      avgScores: {
        avg_brand_index_score: avgIndexScore,
        avg_brand_favorability_score: avgFavorabilityScore,
        avg_brand_reach_score: avgReachScore,
      },
      llms,
    };
  });

  return finalData;
}

export function mergeBrandAndProductData(brandFinalData, productFinalData) {
  // Create a map of brandId to product data for quick lookup
  const productMap = {};

  // Populate the product map by brandId
  productFinalData.forEach((brand) => {
    productMap[brand.brandId] = brand.products;
  });

  // Iterate through brandFinalData and add corresponding products to each brand
  const mergedData = brandFinalData.map((brand) => {
    const productsForBrand = productMap[brand.brandId] || []; // Default to empty array if no products are found

    return {
      ...brand,
      products: productsForBrand, // Add products as a key
    };
  });

  return mergedData;
}

export const getFilteredPurchaseDimensions = (data) => {
  const attributeCount = {};

  for (const brand in data) {
    const attributes = Object.keys(data[brand]);

    attributes.forEach((attr) => {
      if (!attributeCount[attr]) {
        attributeCount[attr] = 0;
      }
      attributeCount[attr] += 1;
    });
  }

  // Step 2: Sort attributes by their occurrence
  const sortedAttributes = Object.keys(attributeCount).sort(
    (a, b) => attributeCount[b] - attributeCount[a]
  );

  // Step 3: Select the top 5 most occurring attributes
  const topAttributes = sortedAttributes.slice(0, 5);

  // Step 4: Filter the data to keep only the top 5 attributes per brand
  const filteredData = {};

  for (const brand in data) {
    filteredData[brand] = {};

    topAttributes.forEach((attr) => {
      if (data[brand][attr]) {
        filteredData[brand][attr] = data[brand][attr];
      }
    });
  }

  return filteredData;
};

export function transformCustomerCoreAttData(data, isCoreAtt) {
  const brandMap = {};

  // Iterate through the data to group by brand and aggregate scores by core attributes
  data.forEach((item) => {
    const brandId = item?.customer_brand_persona?.customer_brand?.brand?.id;
    const customerBrandId = item?.customer_brand_persona?.customer_brand?.id;
    const brandName = item?.customer_brand_persona?.customer_brand?.brand?.name;
    const brandDescription =
      item?.customer_brand_persona?.customer_brand?.brand?.description;
    const brandDomain =
      item?.customer_brand_persona?.customer_brand?.brand?.domain_url;
    const llmName = item?.customer_llm?.llm?.name;
    const coreAttributeName = isCoreAtt
      ? item?.core_attribute?.name
      : item?.category_dimension?.name;
    const avgScore = parseFloat(isCoreAtt ? item?.avg_score : item?.avg_rating);

    if (!brandMap[brandId]) {
      brandMap[brandId] = {
        customerBrandId,
        brandId,
        brandName,
        brandDescription,
        brandDomain,
        llms: {},
        coreAttributes: {},
      };
    }

    // Add LLM and core attribute scores for this brand
    if (!brandMap[brandId].llms[llmName]) {
      brandMap[brandId].llms[llmName] = {};
    }

    if (!brandMap[brandId].llms[llmName][coreAttributeName]) {
      brandMap[brandId].llms[llmName][coreAttributeName] = {
        total: 0,
        count: 0,
      };
    }

    // Aggregate total scores for core attributes
    const llmCoreAttrData = brandMap[brandId].llms[llmName][coreAttributeName];
    llmCoreAttrData.total += avgScore;
    llmCoreAttrData.count += 1;

    // Aggregate total scores for core attributes across all LLMs
    if (!brandMap[brandId].coreAttributes[coreAttributeName]) {
      brandMap[brandId].coreAttributes[coreAttributeName] = {
        total: 0,
        count: 0,
      };
    }

    brandMap[brandId].coreAttributes[coreAttributeName].total += avgScore;
    brandMap[brandId].coreAttributes[coreAttributeName].count += 1;
  });

  // Step 2: Calculate averages after iteration
  Object.values(brandMap).forEach((brand) => {
    Object.keys(brand.llms).forEach((llmName) => {
      Object.keys(brand.llms[llmName]).forEach((coreAttributeName) => {
        const llmCoreAttrData = brand.llms[llmName][coreAttributeName];
        if (llmCoreAttrData.count) {
          brand.llms[llmName][coreAttributeName] = (
            llmCoreAttrData.total / llmCoreAttrData.count
          ).toFixed(0); // Calculate average
        }
      });
    });
  });

  // Prepare final data structure for rendering
  const finalData = Object.values(brandMap).map((brand) => {
    // Calculate average scores for each core attribute
    const avgScores = Object.keys(brand.coreAttributes).reduce(
      (acc, attrName) => {
        const attrData = brand.coreAttributes[attrName];
        const avg = Math.round(attrData.total / attrData.count); // Round to nearest integer
        acc[attrName] = avg;
        return acc;
      },
      {}
    );

    return {
      customerBrandId: brand.customerBrandId,
      brandId: brand.brandId,
      brandName: brand.brandName,
      brandDomain: brand.brandDomain,
      avgScores,
      llms: brand.llms,
    };
  });

  return finalData;
}

export function transformCustomerProductCoreAttData(data, isCoreAtt) {
  const brandProductMap = {};

  // Iterate through the data to group by brand and product, and aggregate scores by core attribute and LLM
  data.forEach((item) => {
    const brandId =
      item?.customer_brand_product_persona?.custProduct?.product?.brand_id;
    const productId =
      item?.customer_brand_product_persona?.custProduct?.product?.id;
    const customerProductId =
      item?.customer_brand_product_persona?.custProduct?.id;
    const productName =
      item?.customer_brand_product_persona?.custProduct?.product?.name;
    const productDescription =
      item?.customer_brand_product_persona?.custProduct?.product?.description;
    const llmName = item?.customer_llm?.llm?.name;
    const coreAttributeName = isCoreAtt
      ? item?.core_attribute?.name
      : item?.category_dimension?.name;
    const avgScore = parseFloat(isCoreAtt ? item?.avg_score : item?.avg_rating);

    // Initialize the brand in the map if it doesn't exist
    if (!brandProductMap[brandId]) {
      brandProductMap[brandId] = {
        brandId,
        products: {},
      };
    }

    // Initialize the product under the brand if it doesn't exist
    if (!brandProductMap[brandId].products[productId]) {
      brandProductMap[brandId].products[productId] = {
        customerProductId,
        productId,
        productName,
        productDescription,
        llms: {},
        coreAttributes: {},
      };
    }

    // Initialize the LLM for this product if it doesn't exist
    if (!brandProductMap[brandId].products[productId].llms[llmName]) {
      brandProductMap[brandId].products[productId].llms[llmName] = {};
    }

    // Add the core attribute score for the current LLM
    if (
      !brandProductMap[brandId].products[productId].llms[llmName][
      coreAttributeName
      ]
    ) {
      brandProductMap[brandId].products[productId].llms[llmName][
        coreAttributeName
      ] = { total: 0, count: 0 };
    }

    // Aggregate total scores for core attributes
    const llmCoreAttrData =
      brandProductMap[brandId].products[productId].llms[llmName][
      coreAttributeName
      ];
    llmCoreAttrData.total += avgScore;
    llmCoreAttrData.count += 1;

    // Aggregate total scores for core attributes across all LLMs
    if (
      !brandProductMap[brandId].products[productId].coreAttributes[
      coreAttributeName
      ]
    ) {
      brandProductMap[brandId].products[productId].coreAttributes[
        coreAttributeName
      ] = { total: 0, count: 0 };
    }

    const coreAttrData =
      brandProductMap[brandId].products[productId].coreAttributes[
      coreAttributeName
      ];
    coreAttrData.total += avgScore;
    coreAttrData.count += 1;
  });

  // Step 2: Calculate averages after the loop
  Object.values(brandProductMap).forEach((brand) => {
    Object.values(brand.products).forEach((product) => {
      Object.keys(product.llms).forEach((llmName) => {
        Object.keys(product.llms[llmName]).forEach((coreAttributeName) => {
          const llmCoreAttrData = product.llms[llmName][coreAttributeName];
          if (llmCoreAttrData.count) {
            product.llms[llmName][coreAttributeName] = (
              llmCoreAttrData.total / llmCoreAttrData.count
            ).toFixed(0); // Calculate average
          }
        });
      });
    });
  });

  // Prepare final data structure for rendering
  const finalData = Object.values(brandProductMap).map((brand) => {
    const products = Object.values(brand.products).map((product) => {
      const avgScores = Object.keys(product.coreAttributes).reduce(
        (acc, attrName) => {
          const attrData = product.coreAttributes[attrName];
          const avg = Math.round(attrData.total / attrData.count); // Round to nearest integer
          acc[attrName] = avg;
          return acc;
        },
        {}
      );

      return {
        customerProductId: product.customerProductId,
        productId: product.productId,
        productName: product.productName,
        productDescription: product.productDescription,
        avgScores, // Include the calculated average scores
        llms: product.llms, // Include the LLMs with core attribute names and scores
      };
    });

    return {
      brandId: brand.brandId,
      products,
    };
  });

  return finalData;
}

export const mergeBrandCoreAttAndIndexData = (data1, data2, data3) => {
  // Create maps for data2 and data3 using brandId for easy lookup
  const data2Map = new Map(data2.map((item) => [item.brandId, item]));
  const data3Map = new Map(data3.map((item) => [item.brandId, item]));

  // Iterate over data1 and merge the relevant properties from data2 and data3
  return data1.map((item) => {
    const correspondingData2 = data2Map.get(item.brandId);
    const correspondingData3 = data3Map.get(item.brandId);

    if (correspondingData2) {
      // Embed coreAttAvgScores and coreAttLlms from data2
      item.coreAttAvgScores = correspondingData2.avgScores;
      item.coreAttLlms = correspondingData2.llms;
    }

    if (correspondingData3) {
      // Embed categoryDimAvgScores and categoryDimLlms from data3
      item.categoryDimAvgScores = correspondingData3.avgScores;
      item.categoryDimLlms = correspondingData3.llms;
    }

    return item;
  });
};

export const mergeBrandProductCoreAttData = (
  brandData,
  productData,
  categoryData
) => {
  // Create maps for productData and categoryData using brandId for easy lookup
  const productDataMap = new Map(
    productData.map((item) => [item.brandId, item])
  );
  const categoryDataMap = new Map(
    categoryData.map((item) => [item.brandId, item])
  );

  // Iterate over brandData and merge the relevant product and category properties
  return brandData.map((brandItem) => {
    const correspondingProductData = productDataMap.get(brandItem.brandId);
    const correspondingCategoryData = categoryDataMap.get(brandItem.brandId);

    if (correspondingProductData) {
      // Merge products for the same brandId
      correspondingProductData.products.forEach((product) => {
        const matchingProduct = brandItem.products.find(
          (p) => p.productId === product.productId
        );

        if (matchingProduct) {
          // Embed coreAttAvgScores and coreAttLlms for the product
          matchingProduct.coreAttAvgScores = product.avgScores;
          matchingProduct.coreAttLlms = product.llms;
        }
      });
    }

    if (correspondingCategoryData) {
      // Merge categories for the same brandId
      correspondingCategoryData.products.forEach((product) => {
        const matchingProduct = brandItem.products.find(
          (p) => p.productId === product.productId
        );

        if (matchingProduct) {
          // Embed categoryDimAvgScores and categoryDimLlms for the product
          matchingProduct.categoryDimAvgScores = product.avgScores;
          matchingProduct.categoryDimLlms = product.llms;
        }
      });
    }

    return brandItem;
  });
};

export function sliceArrayWithPriority(array, keyName, specificName) {
  // Filter objects containing the specific name
  const priorityObjects = array.filter((obj) => obj[keyName] === specificName);

  // Filter the rest of the objects
  const otherObjects = array.filter((obj) => obj[keyName] !== specificName);

  // Slice the remaining objects to make the total count 5
  // const slicedOtherObjects = otherObjects.slice(0, 5 - priorityObjects.length);

  // Combine and return the result
  return [...priorityObjects, ...otherObjects];
}

export function coreAttributesDataTransform(data, specificName) {
  if (data && Object.keys(data)?.length) {
    const result = [];

    // Get the list of unique attribute names from the first brand

    // Collect all unique attribute names from all brands
    const attributes = [
      ...new Set(
        Object.values(data).flatMap((brandObj) => Object.keys(brandObj))
      ),
    ];

    // Iterate over each attribute
    attributes.forEach((attribute) => {
      const attributeObject = {
        name: attribute.toLowerCase(), // Convert attribute name to lowercase
        scores: [],
      };

      // Iterate over each brand in the dataset
      for (let brand in data) {
        if (data[brand][attribute]) {
          const { avgValue, explanation, sources } = data[brand][attribute];

          // Push the brand's score and explanation into the scores array
          attributeObject.scores.push({
            name: brand,
            score: avgValue,
            explanation: explanation,
            sources: sources,
          });
        }
      }
      if (specificName) {
        attributeObject.scores = sliceArrayWithPriority(
          attributeObject.scores,
          "name",
          specificName
        );
      }

      // Add the attribute object to the result array
      result.push(attributeObject);
    });

    return result;
  }
}

export function transformNestedObj(inputObj) {
  const transformedObj = {};

  // Iterate over each key in the input object
  for (const mainKey in inputObj) {
    if (inputObj.hasOwnProperty(mainKey)) {
      // Transform each array of objects into a single object
      transformedObj[mainKey] = inputObj[mainKey].reduce((acc, obj) => {
        acc[obj.name] = obj.value;
        return acc;
      }, {});
    }
  }

  return transformedObj;
}

export function compareAndFindDeltaInLlmScores(
  data,
  currentData,
  pastData,
  threshold,
  isZeroScoreEliminate,
  alertMetricsMapping,
  type,
  llmProviderMap = {}
) {
  // Handle undefined or missing parameters
  if (!data || !currentData || !pastData) {
    return []; // Return an empty array if any parameter is missing or undefined
  }

  // Get all unique attributes from currentData across all LLMs
  const attributes = new Set();
  Object.values(currentData).forEach((llmData) => {
    Object.keys(llmData).forEach((attr) => attributes.add(attr));
  });
  const results = [];

  // Build a provider -> pastLlmName lookup from pastData
  const providerToPastLlm = {};
  for (const pastLlmName in pastData) {
    if (pastData.hasOwnProperty(pastLlmName)) {
      const provider = llmProviderMap[pastLlmName];
      if (provider) {
        providerToPastLlm[provider] = pastLlmName;
      }
    }
  }

  // Loop through each LLM in current data
  for (const llmName in currentData) {
    if (!currentData.hasOwnProperty(llmName)) continue;

    // Find matching past LLM by provider
    const currentProvider = llmProviderMap[llmName];
    const matchedPastLlmName = currentProvider
      ? providerToPastLlm[currentProvider]
      : pastData?.hasOwnProperty(llmName) ? llmName : null;

    if (!matchedPastLlmName || !pastData?.hasOwnProperty(matchedPastLlmName)) continue;

    const isPastDifferentModel = matchedPastLlmName !== llmName;

    // Compare each dynamic attribute score
    attributes.forEach((attr) => {
      const currentScore =
        Number(Number(currentData[llmName]?.[attr]).toFixed(0)) || 0;
      const pastScore =
        Number(Number(pastData[matchedPastLlmName]?.[attr]).toFixed(0)) || 0;

      // Calculate the difference
      const difference = currentScore - pastScore;

      // Check if the difference is greater or equal to the threshold
      if (Math.abs(difference) >= threshold) {
        const status = difference > 0 ? "increased" : "decreased";

        const currentLabel = MONITORING_LLM_LABELS?.[llmName] || llmName;
        const pastLabel = MONITORING_LLM_LABELS?.[matchedPastLlmName] || matchedPastLlmName;

        // Show "PastModel -> CurrentModel" if models differ, otherwise just current
        const modelLabel = isPastDifferentModel
          ? <>{pastLabel} &rarr; {currentLabel}</>
          : currentLabel;

        // Use the attribute name directly without modification
        const message = `${modelLabel} - ${alertMetricsMapping[attr] || attr} ${type}\u00A0${status} by ${Math.abs(difference)}.`;

        if (!isZeroScoreEliminate || (currentScore && pastScore)) {
          // Store the message and status in an object
          results.push({
            message,
            status,
            llmName,
          });
        }
      }
    });
  }

  return results;
}

export function processAlertsData(inputRules, data, alertMetricsMapping, llmProviderMap = {}) {
  let finalResult = [];

  // Loop through each string in the array
  inputRules.forEach((item) => {
    let response = [];

    // Use switch case to call different functions based on the Rule
    switch (item) {
      case "Increase/Decrease RBI Score":
        response =
          compareAndFindDeltaInLlmScores(
            data,
            data.llms,
            data.pastLlms,
            10,
            false,
            alertMetricsMapping,
            "",
            llmProviderMap
          ) || [];
        break;
      case "Increase/Decrease Core Attributes Score":
        response =
          compareAndFindDeltaInLlmScores(
            data,
            data.coreAttLlms,
            data.pastCoreAttLlms,
            2,
            true,
            alertMetricsMapping,
            "Brand Pillar",
            llmProviderMap
          ) || []; // This will now return multiple strings
        break;
      case "Increase/Decrease Purchase Criteria Score":
        response =
          compareAndFindDeltaInLlmScores(
            data,
            data.categoryDimLlms,
            data.pastCategoryDimLlms,
            2,
            true,
            alertMetricsMapping,
            ENTITY_LABELS.PurchaseCriteria,
            llmProviderMap
          ) || [];
        break;
      // case "Increase/Decrease Positive Attribute Signal_Strength":
      //   response =
      //     compareAndFindDeltaInLlmScores(
      //       data,
      //       transformNestedObj(data?.attLlms),
      //       transformNestedObj(data.pastAttLlms),
      //       30,
      //       true,
      //       alertMetricsMapping,
      //       "Positive Attribute"
      //     ) || [];
      //   break;
      // case "Increase/Decrease Negative Attribute Signal_Strength":
      //   response =
      //     compareAndFindDeltaInLlmScores(
      //       data,
      //       transformNestedObj(data.negativeAttLlms),
      //       transformNestedObj(data.pastNegativeAttLlms),
      //       30,
      //       true,
      //       alertMetricsMapping,
      //       "Negative Attribute"
      //     ) || [];
      //   break;
      case "New Top 3 Positive Attributes":
        response =
          compareAndFindNewInLlmAtt(
            data,
            transformNestedObj(data.attLlms),
            transformNestedObj(data.pastAttLlms),
            alertMetricsMapping,
            false,
            true,
            llmProviderMap
          ) || [];
        break;
      case "New Top 3 Negative Attributes":
        response =
          compareAndFindNewInLlmAtt(
            data,
            transformNestedObj(data.negativeAttLlms),
            transformNestedObj(data.pastNegativeAttLlms),
            alertMetricsMapping,
            false,
            false,
            llmProviderMap
          ) || [];
        break;

      case "New Top 10 Branded Sources":
        response =
          compareAndFindNewInLlmSource(
            data,
            data?.llmBrandedSources,
            data?.pastLlmBrandedSources,
            true,
            llmProviderMap
          ) || [];
        break;
      case "New Top 10 Non-Branded Sources":
        response =
          compareAndFindNewInLlmSource(
            data,
            data?.llmNonBrandedSources,
            data?.pastLlmNonBrandedSources,
            false,
            llmProviderMap
          ) || [];
        break;
      // case "New Positive Attribute Arrival":
      //   response =
      //     compareAndFindNewInLlmAtt(
      //       data,
      //       transformNestedObj(data.attLlms),
      //       transformNestedObj(data.pastAttLlms),
      //       alertMetricsMapping,
      //       true,
      //       true
      //     ) || [];
      //   break;
      // case "New Negative Attribute Arrival":
      //   response =
      //     compareAndFindNewInLlmAtt(
      //       data,
      //       transformNestedObj(data.negativeAttLlms),
      //       transformNestedObj(data.pastNegativeAttLlms),
      //       alertMetricsMapping,
      //       true,
      //       false
      //     ) || [];
      //   break;
      default:
        response = []; // Default case if the action is unknown
        break;
    }

    // Spread the response array and push it into the final result array
    finalResult.push(...response);
  });

  const order = [
    "gpt-4o",
    "Gemini",
    "llama3",
    "Claude3",
    "Perplexity",
    "llm-web",
  ];

  return finalResult.sort((a, b) => {
    return order.indexOf(a.llmName) - order.indexOf(b.llmName);
  });
}

export function compareAndFindNewInLlmAtt(
  data,
  currentData,
  pastData,
  alertMetricsMapping,
  isNewAttribute,
  isPositive,
  llmProviderMap = {}
) {
  if (!data || !currentData || !pastData) {
    return [];
  }

  const attributes = new Set();
  Object.values(currentData).forEach((llmData) => {
    Object.keys(llmData).forEach((attr) => attributes.add(attr));
  });

  const results = [];

  const getTopAttributes = (llmData) => {
    return Object.entries(llmData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([key]) => key);
  };

  // Build a provider -> pastLlmName lookup from pastData
  const providerToPastLlm = {};
  for (const pastLlmName in pastData) {
    if (pastData.hasOwnProperty(pastLlmName)) {
      const provider = llmProviderMap[pastLlmName];
      if (provider) {
        providerToPastLlm[provider] = pastLlmName;
      }
    }
  }

  for (const llmName in currentData) {
    if (!currentData.hasOwnProperty(llmName)) continue;

    // Find matching past LLM by provider
    const currentProvider = llmProviderMap[llmName];
    const matchedPastLlmName = currentProvider
      ? providerToPastLlm[currentProvider]
      : pastData?.hasOwnProperty(llmName) ? llmName : null;

    if (!matchedPastLlmName || !pastData?.hasOwnProperty(matchedPastLlmName)) continue;

    const isPastDifferentModel = matchedPastLlmName !== llmName;
    const currentLabel = MONITORING_LLM_LABELS?.[llmName] || llmName;
    const pastLabel = MONITORING_LLM_LABELS?.[matchedPastLlmName] || matchedPastLlmName;

    if (isNewAttribute) {
      for (const attr of attributes) {
        const currentScore = currentData[llmName]?.[attr] || 0;
        const pastScore = pastData[matchedPastLlmName]?.[attr];

        if (pastScore === undefined && currentScore > 50) {
          const modelLabel = isPastDifferentModel
            ? `${pastLabel} → ${currentLabel}`
            : currentLabel;
          const message = `${modelLabel} - New ${isPositive ? " Positive" : " Negative"
            } attribute ${alertMetricsMapping[attr] || attr
            } arrived with ${currentScore}% signal strength.`;
          results.push({ message, status: "newAttribute", llmName });
        }
      }
    } else {
      const currentTop3 = getTopAttributes(currentData[llmName]);
      const pastTop3 = getTopAttributes(pastData[matchedPastLlmName]);

      const newAttributes = currentTop3.filter(
        (attr) => !pastTop3.includes(attr)
      );
      const replacedAttributes = pastTop3.filter(
        (attr) => !currentTop3.includes(attr)
      );

      if (newAttributes.length > 0) {
        const modelLabel = isPastDifferentModel
          ? <>{pastLabel} &rarr; {currentLabel}</>
          : currentLabel;

        const message = (
          <>
            {modelLabel} - {newAttributes.join(", ")}
            {newAttributes.length === 1
              ? " is now in Top 3 "
              : " are now in Top 3"}
            {isPositive ? " Positive" : " Negative"} attributes
            {replacedAttributes.length > 0 && (
              <> , replacing {replacedAttributes.join(", ")}</>
            )}
          </>
        );

        results.push({ message, status: "newTop3", llmName, isPositive });
      }
    }
  }
  return results;
}

export function compareAndFindNewInLlmSource(
  data,
  currentData,
  pastData,
  isBranded,
  llmProviderMap = {}
) {
  if (!data || !currentData || !pastData) {
    return [];
  }

  const attributes = new Set();
  Object.values(currentData).forEach((llmData) => {
    Object.keys(llmData).forEach((attr) => attributes.add(attr));
  });

  const results = [];

  const getTopAttributes = (llmData) => {
    return Object.entries(llmData)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([key]) => key);
  };

  // Build a provider -> pastLlmName lookup from pastData
  const providerToPastLlm = {};
  for (const pastLlmName in pastData) {
    if (pastData.hasOwnProperty(pastLlmName)) {
      const provider = llmProviderMap[pastLlmName];
      if (provider) {
        providerToPastLlm[provider] = pastLlmName;
      }
    }
  }

  for (const llmName in currentData) {
    if (!currentData.hasOwnProperty(llmName)) continue;

    // Find matching past LLM by provider
    const currentProvider = llmProviderMap[llmName];
    const matchedPastLlmName = currentProvider
      ? providerToPastLlm[currentProvider]
      : pastData?.hasOwnProperty(llmName) ? llmName : null;

    if (!matchedPastLlmName || !pastData?.hasOwnProperty(matchedPastLlmName)) continue;

    const currentTop10 = getTopAttributes(currentData[llmName]);
    const pastTop10 = getTopAttributes(pastData[matchedPastLlmName]);

    const newSources = currentTop10.filter(
      (attr) => !pastTop10.includes(attr)
    );
    const replacedSources = pastTop10.filter(
      (attr) => !currentTop10.includes(attr)
    );

    if (newSources.length > 0) {
      results.push({
        status: "newTop10",
        llmName,
        isBranded,
        newSources,
        replacedSources,
        currentTop10,
        pastTop10,
      });
    }
  }
  return results;
}

export function transformData(data) {
  const result = {};

  if (data) {
    for (const [brand, attributes] of Object.entries(data)) {
      result[brand] = {};

      for (const [attribute, details] of Object.entries(attributes)) {
        result[brand][attribute] = details.avgValue;
      }
    }

    return JSON.stringify(result);
  }
}

export const generateDashboardJsonPrompt = (
  entity,
  type,
  RBI,
  Reach,
  Fav,
  positiveAttJson,
  negativeAttJson,
  coreAttJson,
  PurchaseCriteriaJson,
  isProduct
) => {
  const specialMetricNomination = isProduct
    ? "Product Dimension"
    : "Brand Pillar";
  // d)JSON Data for positive attributes of ${entity} across ${type} for the ${type} comparison is :==> ${positiveAttJson}...
  // here 'name' keys value represent positive attribute names and other keys are the ${type}s name and value is the signal strength of them.
  // e)JSON Data for negative attributes of ${entity} across ${type} for the ${type} comparison is :==> ${negativeAttJson}...
  // here 'name' keys value represent attribute name and other keys are the ${type}s name and value is the signal strength of them
  const prompt = `a.1)JSON Data for RBI is:=> ${RBI}..
  a.2) JSON Data for Sentiment is:=> ${Fav}..
  a.3) JSON Data for Reach is:=> ${Reach}..
  in above 3 JSON data.,1st key is brand name and under that ${type} 
  keys with respective score for RBI, Sentiment, and Reach....
  b) JSON Data for ${specialMetricNomination}s is ==> ${transformData(
    isProduct ? processCategoryComparisonTaxonomy(coreAttJson) : coreAttJson
  )}...This object maps ${type} names to their ${specialMetricNomination}s, with each ${specialMetricNomination} holding a corresponding score....
  c) JSON Data for Purchase criterias is ==> ${transformData(
    PurchaseCriteriaJson
  )}...This object maps ${type} names to their Purchase criterias, with each Purchase criteria holding a corresponding score......`;
  return prompt;
};

export const generateCompDashboardJsonPrompt = (
  entity,
  type,
  RBI,
  positiveAttJson,
  negativeAttJson,
  coreAttJson,
  PurchaseCriteriaJson,
  isProduct
) => {
  const specialMetricNomination = isProduct
    ? "Product Dimension"
    : "Brand Pillar";

  // d)JSON Data for positive attributes of ${entity} across ${type} for the ${type} comparison is :==> ${positiveAttJson}...
  // here 'name' keys value represent positive attribute names and other keys are the ${type}s name and value is the signal strength of them.
  // e)JSON Data for negative attributes of ${entity} across ${type} for the ${type} comparison is :==> ${negativeAttJson}...
  // here 'name' keys value represent attribute name and other keys are the ${type}s name and value is the signal strength of them

  const prompt = `a)JSON Data for RBI(Brand Index Score),Favorability(Sentiment) and Rank(Reach) for ${entity} 
  and its competitor brands is:=> ${RBI}..
  b) JSON Data for ${specialMetricNomination}s is ==> ${transformData(
    isProduct ? processCompProductTaxonomy(coreAttJson) : coreAttJson
  )}...This object maps brand/competitor brand names to their ${specialMetricNomination}s, with each ${specialMetricNomination} holding a corresponding score....
  c) JSON Data for Purchase criterias is ==> ${transformData(
    PurchaseCriteriaJson
  )}...This object maps brand/competitor brand names to their Purchase criterias, with each Purchase criteria holding a corresponding score......`;
  return prompt;
};

export const generateSourceDashboardJsonPrompt = (jsonData) => {
  const prompt = `JSON Data for sources is ==> ${jsonData}`;
  return prompt;
};

export const transformSourceAliasData = (arr) => {
  if (arr && arr.length) {
    const aliasMapping = {};
    arr?.forEach((item) => {
      aliasMapping[item?.domain_url] = item?.source_aliases?.map((obj) => {
        return { url: obj?.url, http_status: obj?.http_status };
      });
    });
    return aliasMapping;
  }
};

export function mergeChangeActions(data1, data2) {
  return data1?.map((data1Obj) => {
    // Filter matching objects from data2
    const matchingData = data2
      ?.filter((data2Obj) => {
        return (
          data1Obj.llm === data2Obj.llm &&
          data1Obj.market === data2Obj.market &&
          data1Obj.persona === data2Obj.persona &&
          data1Obj.geography === data2Obj.geography &&
          data1Obj.brand === data2Obj.brand
        );
      })
      .map(({ llm, market, persona, geography, brand, ...rest }) => rest); // Exclude the matching keys

    // Add the reduced matching data to 'changeActionData'
    return {
      ...data1Obj,
      changeActionData: matchingData?.[0],
    };
  });
}

export function sortRecommendedActionsData(data) {
  // Step 1: Extract unique categories in the order they appear
  const categoryOrder = [];
  const seenCategories = new Set();

  data.forEach((item) => {
    if (item.category && !seenCategories.has(item.category)) {
      seenCategories.add(item.category);
      categoryOrder.push(item.category);
    }
  });

  return data.sort((a, b) => {
    // Sort by brand/product
    if (!a.isProduct && b.isProduct) return -1;
    if (a.isProduct && !b.isProduct) return 1;

    // Sort by category based on order in categoryOrder
    const categoryIndexA = categoryOrder.indexOf(a.category);
    const categoryIndexB = categoryOrder.indexOf(b.category);

    if (categoryIndexA !== -1 && categoryIndexB !== -1) {
      return categoryIndexA - categoryIndexB;
    } else if (categoryIndexA !== -1) {
      return -1;
    } else if (categoryIndexB !== -1) {
      return 1;
    }

    // Sort by market
    if (a.market === "General Market" && b.market !== "General Market")
      return -1;
    if (a.market !== "General Market" && b.market === "General Market")
      return 1;

    // Sort by persona
    if (a.persona === "General Persona" && b.persona !== "General Persona")
      return -1;
    if (a.persona !== "General Persona" && b.persona === "General Persona")
      return 1;

    // Sort by geography
    if (a.geography === "USA" && b.geography !== "USA") return -1;
    if (a.geography !== "USA" && b.geography === "USA") return 1;

    return 0; // Fallback to maintain original order
  });
}

// export function sortRecommendedActionsData(data) {

//   const llmOrder = [
//     "gpt-4o",
//     "gpt-4o-web-search",
//     "Gemini",
//     "llama3",
//     "Claude3",
//     "Perplexity"
//   ];

//   return data.sort((a, b) => {
//     // Sort by brand/product
//     if (!a.isProduct && b.isProduct) return -1;
//     if (a.isProduct && !b.isProduct) return 1;

//     // Sort by market
//     if (a.market === "General Market" && b.market !== "General Market")
//       return -1;
//     if (a.market !== "General Market" && b.market === "General Market")
//       return 1;

//     // Sort by persona
//     if (a.persona === "General Persona" && b.persona !== "General Persona")
//       return -1;
//     if (a.persona !== "General Persona" && b.persona === "General Persona")
//       return 1;

//     // Sort by geography
//     if (a.geography === "USA" && b.geography !== "USA") return -1;
//     if (a.geography !== "USA" && b.geography === "USA") return 1;

//     // Sort by LLM using custom order
//     const llmIndexA = llmOrder.indexOf(a.llm);
//     const llmIndexB = llmOrder.indexOf(b.llm);

//     if (llmIndexA !== -1 && llmIndexB !== -1) {
//       return llmIndexA - llmIndexB;
//     } else if (llmIndexA !== -1) {
//       return -1;
//     } else if (llmIndexB !== -1) {
//       return 1;
//     }

//     // If all are equal, maintain original order
//     return 0;
//   });
// }

export function customizeFontSizeAndStyle(
  markdown,
  fontSize,
  headlineFontSize
) {
  const sizeToMarkdownMapping = {
    12: "", // Normal text (default)
    14: "**", // Slightly larger text (bold)
    16: "***", // Even larger (bold italic)
    18: "######", // Larger (header level 3)
    20: "#####", // Larger still (header level 2)
    24: "#", // Largest (header level 1)
  };

  const markdownStyle = sizeToMarkdownMapping[fontSize] || "";
  const markdownHeaderStyle = sizeToMarkdownMapping[headlineFontSize] || "";
  const lines = markdown.split("\n");
  const modifiedLines = lines.map((line) => {
    if (line.trim().startsWith("•")) {
      return `${markdownStyle} ${line}`;
    } else if (line.trim() !== "") {
      return `${markdownHeaderStyle} ${line}`;
    }
    return line;
  });

  return modifiedLines.join("\n");
}

export function transformDriverData(
  driverDataArray,
  sentimentType = "Positive"
) {
  const driverKey = `Top ${sentimentType} Driver`;

  return driverDataArray?.map((item) => ({
    [driverKey]: item.driver,
    Mentions: item.count,
    // "Total Results": item.total,
    "Signal Strength": `${Math.round((item.count / item.total) * 100)}%`,
  }));
}

export function transformFunnelData(data = {}) {
  if (!Object.keys(data) || Object.keys(data).length === 0) return;
  const maxLength = Math.max(
    data.Awareness.length,
    data.Consideration.length,
    data.Decision.length,
    data?.["Post-Purchase"]?.length
  );

  const result = [];

  for (let i = 0; i < maxLength; i++) {
    result.push({
      Awareness: data.Awareness[i]?.qst || "",
      Consideration: data.Consideration[i]?.qst || "",
      Decision: data.Decision[i]?.qst || "",
      "Post-Purchase": data?.["Post-Purchase"][i]?.qst || "",
    });
  }

  return result;
}

export function getUniqueAttributes(data, mainBrand) {
  const attributeMap = {};

  for (const brand in data) {
    const attributes = Object.keys(data[brand]);
    for (const attr of attributes) {
      if (!attributeMap[attr]) {
        attributeMap[attr] = new Set();
      }
      attributeMap[attr].add(brand);
    }
  }

  return Object.entries(attributeMap)
    .filter(([attr, brandSet]) => brandSet.has(mainBrand) && brandSet.size <= 2)
    .map(([attr]) => attr);
}

export const getCleanQuestion = (question) => {
  if (typeof question !== "string") return question;

  let cleanQuestion = question.trim();

  // // Step 1: Trim everything before the main question after "priorities."
  const splitPatterns = [
    /.*that reflects (?:my|his|her|their) specific needs(?:,\s*|\s+)interests(?:,\s*|\s+)and priorities[.!?]*/i,
    /.*qui reflète mes besoins spécifiques(?:[\s\S]*?)priorités[.!?]*/i, // French
    /.*che riflette le mie esigenze specifiche(?:[\s\S]*?)priorità[.!?]*/i, // Italian
  ];

  for (const pattern of splitPatterns) {
    const m = pattern.exec(cleanQuestion);
    if (m) {
      cleanQuestion = cleanQuestion.slice(m.index + m[0].length).trim();
      break;
    }
  }

  // Step 2: Remove everything after instruction keywords (multilingual)
  cleanQuestion = cleanQuestion
    .split(
      /(?:PROMPT INSTRUCTIONS?|INSTRUCTIONS DU PROMPT|ISTRUZIONI DEL PROMPT)\s*:/i
    )[0]
    .trim();

  // Step 3: Remove common instruction patterns in multiple languages
  cleanQuestion = cleanQuestion
    .replace(/Votre réponse doit.*$/i, "")
    .replace(/Créez un.*paragraphe.*$/i, "")
    .replace(/La valeur de.*$/i, "")
    .replace(/Restituisci.*JSON.*$/i, "") // Italian
    .replace(/\s+/g, " ")
    .trim();

  // Step 4: Remove common repetitive prefixes (English + French + Italian)
  const commonPrefixes = [
    /^I am a .+? looking to learn more about .+?\. /i,
    /^Je suis un\(e\) .+? cherchant à en savoir plus sur .+?\. /i,
    /^Sono un\(a\) .+? che vuole saperne di più su .+?\. /i,
    /^As a .+?, /i,
    /^En tant que .+?, /i,
    /^Considerando il punto di vista di .+?, /i,
  ];

  for (const prefix of commonPrefixes) {
    cleanQuestion = cleanQuestion.replace(prefix, "");
  }

  // Step 5: Remove trailing punctuation
  // cleanQuestion = cleanQuestion.replace(/[.!?]+\s*$/, "");

  // Step 6: Capitalize first letter
  if (cleanQuestion.length > 0) {
    cleanQuestion =
      cleanQuestion.charAt(0).toUpperCase() + cleanQuestion.slice(1);
  }

  // Step 7: Ensure question mark for actual questions
  // const questionWords = [
  //   "what",
  //   "how",
  //   "why",
  //   "list",
  //   "compare",
  //   "explain",
  //   "would",
  //   "which",
  //   "quoi", // French
  //   "comment",
  //   "pourquoi",
  //   "quels",
  //   "quelles",
  //   "cosa", // Italian
  //   "come",
  //   "perché",
  //   "qual",
  //   "quali",
  // ];

  // if (
  //   questionWords.some((word) => cleanQuestion.toLowerCase().includes(word))
  // ) {
  //   if (!cleanQuestion.endsWith("?")) {
  //     cleanQuestion += "?";
  //   }
  // }

  console.log("📋 FINAL QUESTION:", cleanQuestion);
  return cleanQuestion;
};

export function generateCombination(groups, keys) {
  function helper(index, current) {
    if (index === groups.length) {
      return [current];
    }

    let result = [];
    for (let item of groups[index]) {
      result = result.concat(
        helper(index + 1, { ...current, [keys[index]]: item })
      );
    }
    return result;
  }

  return helper(0, {});
}

export function getReportDownloadUrl(jobId) {
  const downloadUrl = `${process.env.REACT_APP_BASE_URL}/llm-reporting/download/${jobId}`;
  return downloadUrl;
}

export function upperCaseConverter(str) {
  return str
    .split(/\s+/g) // split string into words
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1) // capitalize first letter
    )
    .join(" ");
}

export function transformSourcesResponse(
  RequestObject,
  llmList,
  category,
  market,
  persona
) {
  const CONSTANT_ALIAS = {
    alias_signal_strength: "2.5",
    alias_query_count: 1,
    is_positive: 0,
  };

  const responsesMap = {}; // merge by domain

  for (const llm of llmList) {
    const llmData = RequestObject[llm];
    if (!llmData) continue;

    const categoryData = llmData[category];
    if (!categoryData) continue;

    const marketData = categoryData[market];
    if (!marketData || typeof marketData !== "object") continue;

    const personaData =
      persona && marketData && typeof marketData === "object" && marketData[persona]
        ? marketData[persona]
        : marketData;

    // Convert object-with-numeric-keys into array
    const itemsArray = Array.isArray(personaData)
      ? personaData
      : Object.values(personaData);

    // Group by domain
    const domainGroups = itemsArray.reduce((acc, item) => {
      if (!item?.url) return acc;

      let domain;
      try {
        domain = new URL(item.url).hostname;
      } catch {
        return acc; // skip invalid URL
      }

      if (!acc[domain]) acc[domain] = [];
      acc[domain].push({
        http_status: String(item.http_status ?? "200"),
        alias_signal_strength: CONSTANT_ALIAS.alias_signal_strength,
        alias_query_count: CONSTANT_ALIAS.alias_query_count,
        is_positive: CONSTANT_ALIAS.is_positive,
        source_alias_url: item.url,
        alias_absolute_count: item.count ?? 1,
        source_alias_id: Math.floor(Math.random() * 100000),
      });

      return acc;
    }, {});

    // Merge per domain into response map
    for (const [domain, aliases] of Object.entries(domainGroups)) {
      if (!responsesMap[domain]) responsesMap[domain] = { name: domain };
      responsesMap[domain][llm] = {
        aliases,
        count: aliases.length,
      };
    }
  }

  return Object.values(responsesMap);
}

export function transformOldSourcesResponse(
  RequestObject,
  llmList,
  category,
  market,
  persona
) {
  const CONSTANT_ALIAS = {
    http_status: "200",
    alias_signal_strength: "2.5",
    alias_query_count: 1,
    is_positive: 0,
  };

  const responsesMap = {}; // to merge domains across LLMs

  for (const llm of llmList) {
    const llmData = RequestObject[llm];
    if (!llmData) continue;

    const categoryData = llmData[category];
    if (!categoryData) continue;
    const marketData = categoryData[market];
    if (!marketData || typeof marketData !== "object") continue;

    const personaData =
      persona && marketData && typeof marketData === "object" && marketData[persona]
        ? marketData[persona]
        : marketData;


    // Group URLs by domain
    const domainGroups = Object.entries(personaData).reduce(
      (acc, [url, count]) => {
        if (typeof url !== "string" || !url.trim()) return acc;

        let domain;
        try {
          domain = new URL(url).hostname;
        } catch {
          return acc; // skip invalid URL
        }

        if (!acc[domain]) acc[domain] = [];
        acc[domain].push({
          ...CONSTANT_ALIAS,
          source_alias_url: url,
          alias_absolute_count: count,
          source_alias_id: Math.floor(Math.random() * 100000), // optional random id
        });

        return acc;
      },
      {}
    );

    // Merge LLM data into response per domain
    for (const [domain, aliases] of Object.entries(domainGroups)) {
      if (!responsesMap[domain]) responsesMap[domain] = { name: domain };

      responsesMap[domain][llm] = {
        aliases,
        count: aliases.length,
      };
    }
  }

  // Convert to array
  return Object.values(responsesMap);
}

export function updateValidatedSources(brandData) {
  if (!brandData || !Array.isArray(brandData.sentimentMeta)) {
    console.warn("Invalid BrandData format");
    return brandData;
  }

  brandData.sentimentMeta = brandData.sentimentMeta.map((innerArray) =>
    innerArray.map((item) => {
      if (Array.isArray(item.sources)) {
        // Extract only URLs with http_status === 200
        const validUrls = item.sources
          .filter((src) => src.http_status === 200)
          .map((src) => src.url);

        // Replace sources array with only valid URLs
        return { ...item, sources: validUrls };
      }
      return item;
    })
  );

  return brandData;
}

export function replacePlaceholders(str, values) {
  return str.replace(/{{\s*(\w*)\s*}}/g, (match, key) => {
    return values[key] !== undefined ? values[key] : "";
  });
}

/* in sentiment question replace {{ product }} to {{ brand/product }}*/
export function sentimentPlaceHolderReplace(question) {
  const regex = /\{\{\s*([^}]+?)\s*\}\}/g;

  return (question ?? "").replace(regex, (match, inside) => {
    const clean = inside.trim().toLowerCase();

    if (["product", "brand", "company"].includes(clean)) {
      return `{{ ${sentimentQuestionReplacement} }}`;
    }

    return match; // leave it unchanged
  });
}

/* in sentiment question replace {{ brand/product }} to {{ product }} */
export function sentimentPlaceHolderReplaceToOrg(question) {
  const regex = /\{\{\s*([^}]+?)\s*\}\}/g;

  return (question ?? "").replace(regex, (match, inside) => {
    const clean = inside.trim().toLowerCase();

    if ([sentimentQuestionReplacement, "brand", "company"].includes(clean)) {
      return `{{ product }}`;
    }

    return match; // leave it unchanged
  });
}
export function extractAttribute({
  type = "visibility",
  metricSettingObject,
  category,
}) {
  /*
    STEP 1:
    NOTE:
    - If `id` is "default", we intentionally skip this block.
    - "default" is handled separately in the final fallback section.
  */
  let id = metricSettingObject?.categoryWiseMetric?.[category];
  let user_metric_setting_id = metricSettingObject?.user_metric_setting_id

  if (id && metricSettingObject?.[id]?.[type] && id !== "default") {
    const list = metricSettingObject?.[id]?.[type] ?? [];
    return list?.map((ele) => ele.attribute);
  } else if (
    /*
    STEP 2: If the category does NOT have an assigned id (`!id`) and the
    user_metric_setting_id is NOT "default", then fetch attributes from:

      metricSettingObject[user_metric_setting_id][type]

    This acts as a secondary fallback when:
    - `id` is missing for the category
    - user-defined metric setting exists and is not "default"
  */
    user_metric_setting_id !== "default" &&
    user_metric_setting_id &&
    metricSettingObject?.[user_metric_setting_id]?.[type] &&
    id !== "default"
  ) {
    const list = metricSettingObject?.[user_metric_setting_id]?.[type] ?? [];
    return list?.map((ele) => ele.attribute);
  } else {
    /*
    STEP 3: FINAL FALLBACK (Default behavior)

    - If `type` is "visibility":
        Return default visibility attributes:
            metricSettingObject["default"]["visibility"]

    - For all other types:
        Return null, because we only provide default fallback for visibility.
  */

    if (type === "visibility" || type === "propensity-visibility") {
      return metricSettingObject?.["default"]?.[type]?.map(
        (list) => list.attribute
      );
    }
    return null;
  }
}

/**
 * Extract and format LLM information from chart data
 * @param {Array} indexScoreDataForLineChart - Chart data containing llmName and date (MM-DD-YYYY format)
 * @param {boolean} isChecked - Whether show change data is checked
 * @param {string} startDate - Optional start date for filtering (MM-DD-YYYY format) - past date
 * @param {string} endDate - Optional end date for filtering (MM-DD-YYYY format) - recent date
 * @returns {string|null} - Formatted LLM info string
 */
export const getLLMInfo = (indexScoreDataForLineChart, isChecked, startDate = null, endDate = null) => {
  if (!indexScoreDataForLineChart || indexScoreDataForLineChart.length === 0) {
    return null;
  }

  // Import LLM_MODEL_MAPPING inline to avoid circular dependencies
  const { LLM_MODEL_MAPPING } = require("./constant");

  // Helper to parse dates in MM-DD-YYYY format
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const [month, day, year] = dateStr.split('-').map(num => parseInt(num));
    return new Date(year, month - 1, day);
  };

  // Filter items that have llmName
  const dataWithLlm = indexScoreDataForLineChart.filter(item => item.llmName);

  // Get display name using mapping
  const getDisplayName = (llmName) => LLM_MODEL_MAPPING?.[llmName] || llmName;

  let currentLLM = null;
  let pastLLM = null;

  // If custom dates are provided, get LLMs for those specific dates
  if (startDate && endDate) {
    const startDateParsed = parseDate(startDate);
    const endDateParsed = parseDate(endDate);

    // Filter records within the date range
    const recordsInRange = dataWithLlm.filter(item => {
      const itemDateStr = item.date || item.name;
      const itemDate = parseDate(itemDateStr);
      const itemTime = itemDate.getTime();
      return itemTime === startDateParsed.getTime() || itemTime === endDateParsed.getTime();
    });

    if (recordsInRange.length > 0) {
      // Sort by date ascending
      const sortedRecordsInRange = [...recordsInRange].sort((a, b) => {
        const dateA = parseDate(a.date || a.name);
        const dateB = parseDate(b.date || b.name);
        return dateA - dateB; // Sort ascending (oldest first)
      });

      // If startDate and endDate are the same, only set current, not past
      if (startDate === endDate) {
        currentLLM = sortedRecordsInRange[0]?.llmName;
        pastLLM = null;
      } else {
        // First record is past (startDate), last record is current (endDate)
        pastLLM = sortedRecordsInRange[0]?.llmName;
        currentLLM = sortedRecordsInRange[sortedRecordsInRange.length - 1]?.llmName;
      }
    }
  } else {
    // If no custom dates, sort all data by date (most recent first)
    const sortedData = [...dataWithLlm].sort((a, b) => {
      const dateA = parseDate(a.date || a.name);
      const dateB = parseDate(b.date || b.name);
      return dateB - dateA; // Sort descending (most recent first)
    });

    // First item is current (most recent)
    currentLLM = sortedData[0]?.llmName;
    // Second item is past
    pastLLM = sortedData.length >= 2 ? sortedData[1]?.llmName : null;
  }

  // If no current LLM found, return null
  if (!currentLLM) return null;

  // If only one LLM or past is not available, show only current
  if (!pastLLM || !isChecked) {
    return getDisplayName(currentLLM);
  }

  // If show change data is checked and we have different LLMs, show "past → current"
  if (isChecked && pastLLM !== currentLLM) {
    return `${getDisplayName(pastLLM)} → ${getDisplayName(currentLLM)}`;
  }

  return getDisplayName(currentLLM);
};

export function formatSectionLabel(key) {
  const label = key.replace(/Dataset$/i, "");
  return label
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Determines color coding for score cells based on comparison with other values
 * @param {number|string} value - The value to determine color for
 * @param {array} allValues - Array of all values to compare against
 * @returns {string} - CSS color value ('inherit', '#dc3545' for red, 'rgb(25, 135, 84)' for green)
 */
export function getColorForScore(value, allValues) {
  const numValue = parseFloat(value);
  
  // Filter out NaN values
  const numValues = allValues
    .map(v => parseFloat(v))
    .filter(v => !isNaN(v));

  if (numValues.length === 0 || isNaN(numValue)) return 'inherit';

  const max = Math.max(...numValues);
  const min = Math.min(...numValues);

  // If all values are equal
  if (max === min) {
    // if (numValue <= 2) return '#dc3545'; // red background
    // if (numValue >= 4) return 'rgb(25, 135, 84)'; // green background
    return 'inherit'; // for 3 - keep default
  }

  // If values are different
  if (numValue === max) return 'rgb(25, 135, 84)'; // green background
  if (numValue === min) return '#dc3545'; // red background
  return 'inherit'; // in-between - keep default (appears as black text)
}
